
const SALT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const Util = require('util');

const libFableServiceProviderBase = require('fable-serviceproviderbase');

const MaterialsLabRouter = require('./Materials-Lab-Router.js');

/**
 * Customer-agnostic substring patterns identifying LIMS-managed report named instances.
 *
 * Heuristic. The customer prefix segment (e.g. 'LADOTD') is intentionally excluded so the
 * check works across customers; future iterations should migrate to a structural marker
 * such as a ReportArchetype-driven membership lookup.
 *
 * @type {ReadonlyArray<string>}
 */
const LIMS_GUID_PATTERNS = Object.freeze([ 'LIMS-ContainerForm', '-MSP-', '-RNI-SO-' ]);
// Backstop for the IDMaterialParent walk — no FK prevents a cyclic/runaway chain.
const MAX_MATERIAL_PARENT_DEPTH = 16;

// Sample-level lab selector InputTypes seeded by _setSampleLabsInDocument. Deliberately excludes the
// per-test lab inputs (MaterialsTestLabSelector / MaterialsTestLabPicker) and the display-only variants.
const LAB_SELECTOR_INPUT_TYPES = Object.freeze([ 'MaterialsLabSelector', 'MaterialsLabPicker', 'LabSelector' ]);
// Long-lived instances (the pict web apps register this service once per page
// session) must pick up LabTestSpecificationSetJoin changes without a reload.
const DEFAULT_ALLOWED_LAB_SCOPE_CACHE_TTL_SECONDS = 60;

/**
 * @param {string} pGUIDReportNamedInstance
 * @return {boolean} true if the GUID matches the LIMS-form heuristic
 */
const isLIMSForm = (pGUIDReportNamedInstance) =>
{
	if (typeof pGUIDReportNamedInstance !== 'string')
	{
		return false;
	}
	for (const tmpPattern of LIMS_GUID_PATTERNS)
	{
		if (pGUIDReportNamedInstance.indexOf(tmpPattern) >= 0)
		{
			return true;
		}
	}
	return false;
};

/**
 * @typedef {object} FormGroupContext
 * @property {Record<string, any>} [ConciergeConfig] - Resolved ConciergeDefaults/Sample
 *   config for the project (e.g. `{ Hashes: ['...'], Enabled: true, CreatePerLab: false }`).
 *   When present, group membership unions any RNI whose Hash appears in `ConciergeConfig.Hashes`.
 */

/**
 * @typedef {object} FormGroupDescriptor
 * @property {string} Identifier - Stable machine identifier for the group
 * @property {string} Name - Human display name
 * @property {string} Description - Short description for the bulk-add UI
 * @property {(pAvailableRNIs: Array<Record<string, any>>, pContext?: FormGroupContext) => Array<Record<string, any>>} getMembers
 *   - Filter `pAvailableRNIs` to the subset belonging to this group given the project context.
 */

/**
 * Resolve the set of RNIs that should be treated as LIMS-managed for a given project context.
 *
 * Union of:
 *   - heuristic match: GUID/Hash contains a `LIMS_GUID_PATTERNS` substring
 *   - configured extras: `pContext.ConciergeConfig.Hashes` — the project-level "extra forms"
 *     list the materials API auto-creates on each sample sync regardless of whether they
 *     match the GUID heuristic. See SampleTestController.syncSampleTests's
 *     `tmpDefaultFormConfigs.Hashes` block.
 *
 * @param {Array<Record<string, any>>} pAvailableRNIs - candidate RNIs (typically the project's available list)
 * @param {FormGroupContext} [pContext]
 * @return {Array<Record<string, any>>} subset of pAvailableRNIs that are LIMS-managed in the given context
 */
const getLIMSFormCandidates = (pAvailableRNIs, pContext) =>
{
	if (!Array.isArray(pAvailableRNIs))
	{
		return [];
	}
	const tmpExtraHashesSource = pContext && pContext.ConciergeConfig && pContext.ConciergeConfig.Hashes;
	const tmpExtraHashes = new Set(Array.isArray(tmpExtraHashesSource) ? tmpExtraHashesSource : []);
	return pAvailableRNIs.filter((pRNI) =>
	{
		if (!pRNI)
		{
			return false;
		}
		if (isLIMSForm(pRNI.GUIDReportNamedInstance) || isLIMSForm(pRNI.Hash))
		{
			return true;
		}
		if (pRNI.Hash && tmpExtraHashes.has(pRNI.Hash))
		{
			return true;
		}
		return false;
	});
};

/**
 * Registry of named report-named-instance groups consumable by tooling that wants to
 * present forms in semantic clusters (e.g. the forms-editor bulk-add modal).
 *
 * Group descriptors expose `getMembers(pAvailableRNIs, pContext)` rather than a static
 * predicate so groups whose membership depends on project-level state (e.g. LIMS, where
 * "extra forms" are configured per-project via ConciergeDefaults/Sample.Hashes) can
 * resolve correctly. Groups with purely static membership simply ignore `pContext`.
 *
 * @type {Readonly<Record<string, FormGroupDescriptor>>}
 */
const FORM_GROUPS = Object.freeze(
{
	LIMS: Object.freeze(
	{
		Identifier: 'LIMS',
		Name: 'LIMS Forms',
		Description: 'Forms managed by the Lab Information Management System concierge auto-creation pipeline.',
		getMembers: getLIMSFormCandidates,
	}),
});

class MaterialsFormManager extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The Fable instance
	 * @param {Record<string, any>} [pOptions] - (optional) Options for the adapter
	 * @param {string} [pServiceHash] - (optional) Hash to use for the service
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { HeadlightRestClient: import('headlight-rest-client'), AppData: Record<string, any> }} */
		this.fable;

		if (!this.fable.AppData)
		{
			this.fable.AppData = {};
		}
		/** @type {import('headlight-rest-client')} */
		this._Client = null;

		this.serviceType = 'MaterialsFormManager';
		this.fable.addServiceType('ManifestPackagingService', require('manifest-packaging-service'));
		this.fable.instantiateServiceProviderIfNotExists('ManifestPackagingService', require('manifest-packaging-service'));

		/** @type {Record<number, { CachedAt: number, AllowedLabIDs: Set<number>|null }>} */
		this._AllowedLabIDsByContract = {};

		/** @type {MaterialsLabRouter|null} */
		this._LabRouter = null;
	}

	/**
	 * The lab router deciding which lab a sample ships to and which bench runs each of its tests.
	 *
	 * A customer needing behavior the declarative `LabRouting` rules cannot express names a registered fable
	 * service in `LabRouting.ResolverService`; it must answer `selectSampleLabID` / `selectTestLabID` with
	 * the same shape. An unresolvable name falls back to the built-in router rather than failing the sync.
	 *
	 * @param {Record<string, any>} [pLabRoutingConfig] - the ConciergeDefaults `LabRouting` block
	 *
	 * @return {MaterialsLabRouter}
	 */
	getLabRouter(pLabRoutingConfig)
	{
		if (this._LabRouter)
		{
			return this._LabRouter;
		}
		const tmpResolverService = pLabRoutingConfig && pLabRoutingConfig.ResolverService;
		if (tmpResolverService)
		{
			let tmpCustomRouter = this.fable.services[tmpResolverService];
			if (!tmpCustomRouter && (tmpResolverService in this.fable.serviceManager.servicesMap))
			{
				tmpCustomRouter = this.fable.serviceManager.instantiateServiceProvider(tmpResolverService, this.options);
			}
			if (tmpCustomRouter && typeof tmpCustomRouter.selectTestLabID === 'function')
			{
				this.fable.log.info(`Lab routing is delegated to the customer resolver service '${tmpResolverService}'.`);
				this._LabRouter = tmpCustomRouter;
			}
			else
			{
				this.fable.log.warn(`Lab routing resolver service '${tmpResolverService}' is not registered (or does not implement selectTestLabID); using the built-in router.`);
			}
		}
		if (!this._LabRouter)
		{
			this._LabRouter = new MaterialsLabRouter(this.fable, this.options);
		}
		// Bound lazily rather than at construction so the router reads through whatever session the manager
		// is impersonating at call time.
		this._LabRouter.setDataReaders(
			{
				get: (pURI) => this._get(pURI),
				getAll: (pURI) => this._getAll(pURI),
			});
		return this._LabRouter;
	}

	/**
	 * @return {import('headlight-rest-client')}
	 */
	get client()
	{
		if (this.options.UseSiloedClient)
		{
			if (!this._Client)
			{
				this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
				this._Client = this.fable.serviceManager.instantiateServiceProviderWithoutRegistration('HeadlightRestClient', this.options);
			}
			return this._Client;
		}

		let tmpClient = this.fable.services.HeadlightRestClient;
		if (!tmpClient)
		{
			this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				this.options.HeadlightAPIURL = 'https://api.qa.headlight.com/1.0/';
			}
			tmpClient = this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', this.options);
		}
		return tmpClient;
	}

	/**
	 * Async wrapper for HTTP get.
	 *
	 * @param {String} uri The URI to get.
	 *
	 * @return {Promise<any>} The result of the get.
	 */
	async _get(uri)
	{
		this.fable.log.info(`GET ${uri}`);
		return Util.promisify(this.client.getJSON.bind(this.client))(uri);
	}

	/**
	 * @param {String} pURI
	 * @param {number} [pPageSize=250]
	 *
	 * @return {Promise<Array<any>>}
	 */
	async _getAll(pURI, pPageSize = 250)
	{
		let tmpResults = [];
		let tmpOffset = 0;
		let tmpPage;
		do
		{
			const tmpPageURI = `${pURI}/${tmpOffset}/${pPageSize}`;
			tmpPage = await this._get(tmpPageURI);
			if (!Array.isArray(tmpPage))
			{
				throw new Error(`Expected array response from GET ${tmpPageURI}`);
			}
			tmpResults = tmpResults.concat(tmpPage);
			tmpOffset += pPageSize;
		}
		while (tmpPage.length >= pPageSize);
		return tmpResults;
	}

	/**
	 * Async wrapper for HTTP put.
	 *
	 * @param {String} uri The URI to put to.
	 * @param {any} payload The payload to put (as JSON).
	 *
	 * @return {Promise<any>} The result of the put.
	 */
	async _put(uri, payload)
	{
		this.fable.log.info(`PUT ${uri}`, { payload });
		return Util.promisify(this.client.putJSON.bind(this.client))(uri, payload);
	}

	/**
	 * Async wrapper for HTTP post.
	 *
	 * @param {String} uri The URI to post to.
	 * @param {any} payload The payload to post (as JSON).
	 *
	 * @return {Promise<Object>} The result of the post.
	 */
	async _post(uri, payload)
	{
		this.fable.log.info(`POST ${uri}`, { payload });
		return Util.promisify(this.client.postJSON.bind(this.client))(uri, payload);
	}

	/**
	 * Async wrapper for HTTP delete.
	 *
	 * @param {string} entityType The entity type to delete.
	 * @param {string|number} entityID The entity ID to delete.
	 *
	 * @return {Promise<Object>} The result of the delete.
	 */
	async _deleteEntity(entityType, entityID)
	{
		this.fable.log.info(`DELETE ${entityType}:${entityID}`, { entityType, entityID });
		return Util.promisify(this.client.deleteEntity.bind(this.client))(entityType, entityID);
	}

	/**
	 * @param {Record<string, any>} pManifestSchema
	 *
	 * @return {void}
	 */
	_decorateManifestSchema(pManifestSchema)
	{
		switch (pManifestSchema.Type)
		{
			case 'Test':
				pManifestSchema.DisplayName = `Test Hash: ${pManifestSchema.SubType}`;
				pManifestSchema.TestHash = pManifestSchema.SubType;
				break;
			case 'Header':
				pManifestSchema.DisplayName = `Form Header: ${pManifestSchema.Title}`;
				break;
			case 'Footer':
				pManifestSchema.DisplayName = `Form Footer: ${pManifestSchema.Title}`;
				break;
			default:
				pManifestSchema.DisplayName = pManifestSchema.Title;
		}
		pManifestSchema.Enabled = true;
	}

	// things we need:
	// * get default manifests for a given IDMaterial
	// * get available RNIs and extract test and material mappings?
	//   * TODO: how do we map materials to RNIs? maybe we can put that in the RNI for now? but not long term
	// * return a summary of tests for a given material so it can be visualized
	// * function to apply a subset of the tests (manifests to one or more documents, RNIs as new forms)

	/**
	 * @param {Array<number>} pIDMaterials
	 *
	 * @return {Promise<Array<Record<string, any>>>} The manifests for the given IDMaterial
	 */
	async getManifestsForIDMaterial(pIDMaterials)
	{
		// load MaterialManifestSetJoins for given IDMaterial
		const tmpMaterialManifestSetJoins = await this._getAll(`MaterialManifestSetJoins/FilteredTo/FBL~IDMaterial~INN~${pIDMaterials}`);
		// then load ManifestSets recursively using ManifestSetManifestSetJoin
		let tmpManifestSets = await this._getAll(`ManifestSets/FilteredTo/FBL~IDManifestSet~INN~${tmpMaterialManifestSetJoins.map((join) => join.IDManifestSet).join(',')}`);
		let tmpManifestSchemas = [];
		if (tmpManifestSets.length > 0)
		{
			let tmpLoadedJoins;
			let tmpManifestSetIDs = tmpManifestSets.map((ms) => ms.IDManifestSet);
			const tmpSeenManifestSetIDs = new Set(tmpManifestSetIDs);
			let tmp
			do
			{
				tmpLoadedJoins = await this._getAll(`ManifestSetManifestSetJoins/FilteredTo/FBL~IDManifestSet~INN~${tmpManifestSetIDs.map((ms) => ms.IDManifestSet).join(',')}~FSF~Ordinal~ASC~0`);
				tmpManifestSetIDs = tmpLoadedJoins.map((join) => join.IncludedIDManifestSet).filter((id) => !tmpSeenManifestSetIDs.has(id));
				tmpManifestSetIDs.forEach((id) => tmpSeenManifestSetIDs.add(id));
				if (tmpManifestSetIDs.length > 0)
				{
					tmpManifestSets = tmpManifestSets.concat(await this._getAll(`ManifestSets/FilteredTo/FBL~IDManifestSet~INN~${tmpManifestSetIDs.join(',')}`));
				}
			}
			while (tmpLoadedJoins.length > 0);

			// load ManifestSetManifestSchemaJoins in bulk for the loaded ManifestSets
			const tmpManifestSetManifestSchemaJoins = await this._getAll(`ManifestSetManifestSchemaJoins/FilteredTo/FBL~IDManifestSet~INN~${tmpManifestSets.map((ms) => ms.IDManifestSet).join(',')}~FSF~Ordinal~ASC~0`);
			//FIXME: sort the sets then joins by Ordinal to enforce order on the returned records
			// load ManifestSchema records as we go using ManifestSetManifestSchemaJoin
			tmpManifestSchemas = await this._getAll(`ManifestSchemas/FilteredTo/FBL~IDManifestSchema~INN~${tmpManifestSetManifestSchemaJoins.map((join) => join.IDManifestSchema).join(',')}`);
		}
		// format the results (decide the shape)a
		for (const tmpManifestSchema of tmpManifestSchemas)
		{
			tmpManifestSchema.Enabled = true;
			this._decorateManifestSchema(tmpManifestSchema);
		}
		return tmpManifestSchemas;
	}

	/**
	 * @param {number|string} [pIDProject]
	 *
	 * @return {Promise<Array<Record<string, any>>>} The available RNIs for the given project
	 */
	async _getAvailableRNIs(pIDProject)
	{
		const tmpIDProject = pIDProject ? pIDProject : await this._get(`Projects/0/1`).then((res) => res[0].IDProject);
		if (!tmpIDProject)
		{
			throw new Error(`Could not determine a valid project ID to load RNIs for.`);
		}
		return this._getAll(`ReportNamedInstancesByProject/${tmpIDProject}`, 100);
	}

	/**
	 * Walk a material up its IDMaterialParent chain, returning the material and
	 * every ancestor (so tests/labs assigned to a parent material flow through to
	 * a child-material sample). The walk is guarded against cyclic or
	 * self-referential IDMaterialParent data (no FK prevents it): a repeat id or
	 * the depth cap stops the walk and logs, degrading to "use what we walked"
	 * rather than looping forever.
	 *
	 * @param {number} pIDMaterial
	 *
	 * @return {Promise<Array<number>>} The given material and its parent materials
	 */
	async _resolveMaterialParents(pIDMaterial)
	{
		let tmpIDMaterial = pIDMaterial;
		const tmpMaterialIDs = [];
		const tmpVisited = new Set();
		while (tmpIDMaterial && !tmpVisited.has(tmpIDMaterial) && tmpMaterialIDs.length < MAX_MATERIAL_PARENT_DEPTH)
		{
			tmpVisited.add(tmpIDMaterial);
			tmpMaterialIDs.push(tmpIDMaterial);
			const tmpMaterialRecord = await this._get(`Material/${tmpIDMaterial}`);
			//TODO: if the starting material doesn't exist, do we want to throw?
			tmpIDMaterial = tmpMaterialRecord && tmpMaterialRecord.IDMaterialParent;
		}
		if (tmpIDMaterial && tmpVisited.has(tmpIDMaterial))
		{
			this.fable.log.warn(`Material parent chain for ${pIDMaterial} hit a cycle at material ${tmpIDMaterial}; stopping walk`, { ResolvedMaterialIDs: tmpMaterialIDs });
		}
		else if (tmpIDMaterial && tmpMaterialIDs.length >= MAX_MATERIAL_PARENT_DEPTH)
		{
			this.fable.log.warn(`Material parent chain for ${pIDMaterial} exceeded depth ${MAX_MATERIAL_PARENT_DEPTH}; stopping walk`, { ResolvedMaterialIDs: tmpMaterialIDs });
		}
		return tmpMaterialIDs;
	}

	/**
	 * Index rows carrying `IDTest` + `IDLab` (TestLabJoin, LabTestDefault) into the per-test lab sets the
	 * router narrows with.
	 *
	 * @param {Array<Record<string, any>>} pRows
	 *
	 * @return {Record<string, Set<number>>}
	 */
	_indexLabIDsByTest(pRows)
	{
		/** @type {Record<string, Set<number>>} */
		const tmpIndex = {};
		for (const tmpRow of (pRows || []))
		{
			const tmpIDTest = (tmpRow.IDTest === null || tmpRow.IDTest === undefined) ? '' : String(tmpRow.IDTest).trim();
			const tmpIDLab = parseInt(tmpRow.IDLab, 10);
			if (tmpIDTest === '' || !(tmpIDLab > 0))
			{
				continue;
			}
			if (!tmpIndex[tmpIDTest])
			{
				tmpIndex[tmpIDTest] = new Set();
			}
			tmpIndex[tmpIDTest].add(tmpIDLab);
		}
		return tmpIndex;
	}

	/**
	 * @param {number|string} pIDSample
	 *
	 * @return {Promise<Array<number>>} The lab IDs associated with the sample
	 */
	async getSampleLabIDs(pIDSample)
	{
		return this._getAll(`SampleLabJoins/FilteredTo/FBV~IDSample~EQ~${pIDSample}`).then((res) => res.map((join) => join.IDLab));
	}

	/**
	 * The line items a sample is tested for: its SampleLineItemJoin rows. `Sample.IDLineItem` is deliberately
	 * ignored — the field apps leave it at 0 and record line items only as joins.
	 *
	 * @param {number} pIDSample
	 *
	 * @return {Promise<Array<number>>} unique positive line item ids (empty when the sample has none)
	 */
	async getSampleLineItemIDs(pIDSample)
	{
		const tmpJoins = Number(pIDSample) > 0 ? await this._getAll(`SampleLineItemJoins/FilteredTo/FBV~IDSample~EQ~${Number(pIDSample)}`) : [];
		return MaterialsFormManager.normalizeLineItemIDs(tmpJoins.map((pJoin) => pJoin.IDLineItem));
	}

	/**
	 * @param {number|Array<number>|null|undefined} pLineItemIDs
	 *
	 * @return {Array<number>} unique positive line item ids, in first-seen order
	 */
	static normalizeLineItemIDs(pLineItemIDs)
	{
		const tmpList = Array.isArray(pLineItemIDs) ? pLineItemIDs : [ pLineItemIDs ];
		return Array.from(new Set(tmpList.map((pID) => Number(pID)).filter((pID) => Number.isInteger(pID) && pID > 0)));
	}

	/**
	 * Resolve the contract's materials specification set ID. The explicit
	 * MaterialIDTestSpecificationSet FK wins; when unset, fall back to matching a
	 * TestSpecificationSet by the contract's MaterialsSpecificationYear.
	 *
	 * @param {Record<string, any>} pContract
	 * @return {Promise<number>} The set ID, or 0 when the contract references none
	 */
	async _resolveMaterialSpecificationSetID(pContract)
	{
		if (pContract.MaterialIDTestSpecificationSet > 0)
		{
			return pContract.MaterialIDTestSpecificationSet;
		}
		const tmpYear = pContract.MaterialsSpecificationYear == null ? '' : String(pContract.MaterialsSpecificationYear).trim();
		if (tmpYear === '' || tmpYear === '0')
		{
			return 0;
		}
		// A year is NOT unique: a customer may run a provisional set alongside the live one for the same year,
		// and nothing on TestSpecificationSet distinguishes them. Lowest id — the earliest set for the year —
		// is the stable pick; an ambiguous year is logged rather than resolved silently.
		const tmpSets = await this._getAll(`TestSpecificationSets/FilteredTo/FBV~IDCustomer~EQ~${pContract.IDCustomer}~FBV~Year~EQ~${encodeURIComponent(tmpYear)}~FSF~IDTestSpecificationSet~ASC~0`);
		if (tmpSets.length > 1)
		{
			this.fable.log.warn(`Specification year ${tmpYear} matches ${tmpSets.length} specification sets for customer ${pContract.IDCustomer}; using the lowest id (${tmpSets[0].IDTestSpecificationSet}). Set the contract's MaterialIDTestSpecificationSet to resolve this explicitly.`);
		}
		return tmpSets.length > 0 ? tmpSets[0].IDTestSpecificationSet : 0;
	}

	/**
	 * Labs assignable under the contract's materials specification set, per
	 * LabTestSpecificationSetJoin. Fails open — returns null (no constraint) when
	 * the contract resolves no materials set, the set has no lab joins, or the
	 * read fails — matching the sample form's lab-picker scoping.
	 *
	 * Cached per contract for options.AllowedLabScopeCacheTTLSeconds (default 60).
	 *
	 * @param {Record<string, any>} pContract
	 * @return {Promise<Set<number>|null>} Allowed lab IDs, or null when unconstrained
	 */
	async getAllowedLabIDsForContract(pContract)
	{
		if (!pContract || !(pContract.IDContract > 0))
		{
			return null;
		}
		const tmpTTLSeconds = (typeof this.options.AllowedLabScopeCacheTTLSeconds === 'number') ? this.options.AllowedLabScopeCacheTTLSeconds : DEFAULT_ALLOWED_LAB_SCOPE_CACHE_TTL_SECONDS;
		const tmpCacheEntry = this._AllowedLabIDsByContract[pContract.IDContract];
		if (tmpCacheEntry && (Date.now() - tmpCacheEntry.CachedAt) < (tmpTTLSeconds * 1000))
		{
			return tmpCacheEntry.AllowedLabIDs;
		}
		let tmpAllowedLabIDs = null;
		try
		{
			const tmpSpecSetID = await this._resolveMaterialSpecificationSetID(pContract);
			if (tmpSpecSetID > 0)
			{
				const tmpJoins = await this._getAll(`LabTestSpecificationSetJoins/FilteredTo/FBV~IDTestSpecificationSet~EQ~${tmpSpecSetID}`);
				const tmpJoinedLabIDs = new Set(tmpJoins.map((pJoin) => pJoin.IDLab).filter((pIDLab) => pIDLab > 0));
				if (tmpJoinedLabIDs.size > 0)
				{
					tmpAllowedLabIDs = tmpJoinedLabIDs;
				}
			}
		}
		catch (pError)
		{
			this.fable.log.warn(`Failed to resolve the assignable-lab scope for contract ${pContract.IDContract}; failing open: ${pError.message}`);
		}
		this._AllowedLabIDsByContract[pContract.IDContract] = { CachedAt: Date.now(), AllowedLabIDs: tmpAllowedLabIDs };
		return tmpAllowedLabIDs;
	}

	/**
	 * Resolve the default lab for a material and sample type using LabTestDefaults.
	 * This mirrors the auto-select toggle behavior in the materials-sampletracking app's
	 * distilled-field component, which determines default labs from LabTestDefault records
	 * and resolves them to their parent lab ID.
	 *
	 * When a project is provided, candidates are constrained to the contract's
	 * materials-specification-set lab joins (see getAllowedLabIDsForContract); a
	 * candidate qualifies when it or its parent lab is joined.
	 *
	 * @param {number} pIDMaterial - The material ID
	 * @param {string} [pSampleType='ACPT'] - The sample type
	 * @param {number} [pIDProject=0] - Project whose contract scopes assignable labs
	 * @param {Record<string, any>} [pConciergeConfig] - the project's ConciergeDefaults config (supplies LabRouting)
	 *
	 * @return {Promise<number>} The default parent lab ID, or 0 if none found
	 */
	async getDefaultLabForMaterial(pIDMaterial, pSampleType = 'ACPT', pIDProject = 0, pConciergeConfig = undefined)
	{
		const tmpResult = await this.getDefaultLabForMaterialWithDiagnostics(pIDMaterial, pSampleType, pIDProject, pConciergeConfig);
		return tmpResult.IDLab;
	}

	/**
	 * Same resolution as {@link getDefaultLabForMaterial}, but surfaces the bare-0
	 * causes as a structured Reason so callers can report why no default applied:
	 *   'OK'                        — a default parent lab resolved
	 *   'NoDefaults'                — no LabTestDefault (or lab record) for the material/sample-type,
	 *                                 and no routing rule named a lab for the project either
	 *   'ExcludedBySpecSetLabJoins' — defaults exist, but every candidate lab falls
	 *                                 outside the contract's materials-specification-set lab joins
	 *
	 * Which of several candidates wins is decided by the lab router: a parent lab the project's routing
	 * rules name (its district laboratory before its field unit, for a DOT) outranks one of the project's
	 * own joined labs, which outranks the first candidate the read happened to return. With routing rules
	 * configured this also answers where it previously could not — a material carrying no LabTestDefault
	 * still ships to the project's own lab — so configuring them only ever ADDS defaulting.
	 *
	 * @param {number} pIDMaterial - The material ID
	 * @param {string} [pSampleType='ACPT'] - The sample type
	 * @param {number} [pIDProject=0] - Project whose contract scopes assignable labs
	 * @param {Record<string, any>} [pConciergeConfig] - the project's ConciergeDefaults config (supplies LabRouting)
	 *
	 * @return {Promise<{ IDLab: number, Reason: string, CandidateLabIDs: Array<number>, Source: string }>}
	 */
	async getDefaultLabForMaterialWithDiagnostics(pIDMaterial, pSampleType = 'ACPT', pIDProject = 0, pConciergeConfig = undefined)
	{
		const tmpLabRoutingConfig = pConciergeConfig && pConciergeConfig.LabRouting;
		const tmpRouter = this.getLabRouter(tmpLabRoutingConfig);
		const tmpAffinityRootIDs = await tmpRouter.getAffinityRootIDs(pIDProject, tmpLabRoutingConfig);
		const tmpProjectRootLabIDs = tmpLabRoutingConfig ? await this._getProjectRootLabIDs(pIDProject, tmpRouter) : [];
		const fSelect = (pCandidateLabIDs, pCandidateDiagnosticIDs) =>
		{
			const tmpSelection = tmpRouter.selectSampleLabID(pCandidateLabIDs, { AffinityRootIDs: tmpAffinityRootIDs, ProjectRootLabIDs: tmpProjectRootLabIDs });
			if (!(tmpSelection.IDLab > 0))
			{
				return { IDLab: 0, Reason: 'NoDefaults', CandidateLabIDs: pCandidateDiagnosticIDs, Source: 'None' };
			}
			return { IDLab: tmpSelection.IDLab, Reason: 'OK', CandidateLabIDs: pCandidateDiagnosticIDs, Source: tmpSelection.Source };
		};
		if (!pIDMaterial || pIDMaterial <= 0)
		{
			return fSelect([], []);
		}
		const tmpMaterialIDs = await this._resolveMaterialParents(pIDMaterial);
		const tmpLabTestDefaults = await this._getAll(`LabTestDefaults/FilteredTo/FBL~IDMaterial~INN~${tmpMaterialIDs.join(',')}~FBV~SampleType~EQ~${pSampleType}`);
		if (tmpLabTestDefaults.length < 1)
		{
			return fSelect([], []);
		}
		// Get unique lab IDs from the defaults
		const tmpLabIDs = [...new Set(tmpLabTestDefaults.map((ltd) => ltd.IDLab))];
		// Fetch the lab records to resolve parent labs
		let tmpLabs = await this._getAll(`Labs/FilteredTo/FBL~IDLab~INN~${tmpLabIDs.join(',')}`);
		if (tmpLabs.length < 1)
		{
			return fSelect([], tmpLabIDs);
		}
		if (pIDProject > 0)
		{
			const tmpProject = await this._get(`Project/${pIDProject}`);
			const tmpContract = tmpProject && tmpProject.IDContract > 0 ? await this._get(`Contract/${tmpProject.IDContract}`) : null;
			const tmpAllowedLabIDs = await this.getAllowedLabIDsForContract(tmpContract);
			if (tmpAllowedLabIDs)
			{
				tmpLabs = tmpLabs.filter((pLab) => tmpAllowedLabIDs.has(pLab.IDLab) || tmpAllowedLabIDs.has(pLab.IDLabParent));
				if (tmpLabs.length < 1)
				{
					this.fable.log.info(`All default labs for material ${pIDMaterial} fall outside the contract's materials-specification-set lab joins; no default lab resolved`);
					return { IDLab: 0, Reason: 'ExcludedBySpecSetLabJoins', CandidateLabIDs: tmpLabIDs, Source: 'None' };
				}
			}
		}
		// The router promotes through its own lab tree, matching the web app's indexing by IDLabParent. A lab
		// the catalog does not carry has no tree to promote through, so its own parent pointer stands in.
		await tmpRouter.loadLabCatalog();
		const tmpCandidateRootIDs = tmpLabs.map((pLab) => tmpRouter.getLab(pLab.IDLab) ? pLab.IDLab : (pLab.IDLabParent > 0 ? pLab.IDLabParent : pLab.IDLab));
		return fSelect(tmpCandidateRootIDs, tmpLabIDs);
	}

	/**
	 * The project's own PARENT labs, from `ProjectLabJoin`. A customer that joins a bench instead of its
	 * parent still scopes to the right family, because the ids are normalized through the lab tree.
	 *
	 * NB: `ProjectLabJoin` has no `Deleted` column — foxhound's soft-delete predicate is schema-driven, so a
	 * hand-written `Deleted` clause would error the read and silently fail open to no project labs.
	 *
	 * @param {number} pIDProject
	 * @param {MaterialsLabRouter} pLabRouter - the router whose lab tree normalizes the joined ids
	 *
	 * @return {Promise<Array<number>>}
	 */
	async _getProjectRootLabIDs(pIDProject, pLabRouter)
	{
		if (!(pIDProject > 0))
		{
			return [];
		}
		const tmpJoins = await this._getAll(`ProjectLabJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}`);
		await pLabRouter.loadLabCatalog();
		return Array.from(pLabRouter.rootsOf(tmpJoins.map((pJoin) => pJoin.IDLab)));
	}

	/**
	 * @param {number} pIDMaterial
	 * @param {number} pIDProject
	 * @param {string} [pSampleType='ACPT']
	 * @param {Array<number>} [pSelectedParentLabIDs=undefined]
	 * @param {Record<string, any>} [pConciergeConfig] - the project's ConciergeDefaults config (supplies LabRouting)
	 * @param {number|Array<number>} [pLineItemIDs] - the sample's line item(s) (see getSampleLineItemIDs); default
	 *   tests are scoped to them (none resolve without one)
	 *
	 * @return {Promise<Array<Record<string, any>>>} The default tests by material.
	 */
	async getMaterialTestDefaults(pIDMaterial, pIDProject, pSampleType = 'ACPT', pSelectedParentLabIDs = undefined, pConciergeConfig = undefined, pLineItemIDs = undefined)
	{
		const tmpResult = await this.getMaterialTestDefaultsWithDiagnostics(pIDMaterial, pIDProject, pSampleType, pSelectedParentLabIDs, pConciergeConfig, pLineItemIDs);
		return tmpResult.Tests;
	}

	/**
	 * Same resolution as {@link getMaterialTestDefaults}, but returns the bare
	 * `[]` causes as a structured Reason instead of an empty array — so callers
	 * (the LIMS concierge) can tell "material is not in this contract's spec
	 * year" apart from "configured but nothing to create". Reason is one of:
	 *   'OK'              — at least one default test resolved
	 *   'NoContract'      — the project has no contract (no spec year to draw from)
	 *   'NoMaterial'      — no material was given, so neither the sampling plan nor
	 *                       the spec-year fallback can resolve anything
	 *   'NoLineItem'      — no line item was given; the sampling plan and the
	 *                       spec-year fallback are both line-item scoped, so nothing resolves
	 *   'NoSamplingPlan'  — contract present, but no ProjectLineItemMaterialTest
	 *                       row for this line item/material/sample-type (the spec-year miss)
	 *   'TestsUnresolved' — sampling-plan rows present, but none resolved to a
	 *                       live Test record
	 *
	 * Each resolved test is routed to exactly one lab — a BENCH (leaf) where the capability tables name one,
	 * the sample's parent lab otherwise — so the returned entries partition across `LabID` rather than
	 * repeating a test under several labs.
	 *
	 * @param {number} pIDMaterial
	 * @param {number} pIDProject
	 * @param {string} [pSampleType='ACPT']
	 * @param {Array<number>} [pSelectedParentLabIDs=undefined]
	 * @param {Record<string, any>} [pConciergeConfig] - the project's ConciergeDefaults config (supplies LabRouting)
	 * @param {number|Array<number>} [pLineItemIDs] - the sample's line item(s), normally its SampleLineItemJoins
	 *   (see getSampleLineItemIDs). Sampling-plan rows and the spec-year fallback are both scoped to them; with
	 *   none, no default tests resolve (Reason 'NoLineItem').
	 * @return {Promise<{ Tests: Array<Record<string, any>>, Reason: string, Diagnostics: Record<string, any> }>}
	 */
	async getMaterialTestDefaultsWithDiagnostics(pIDMaterial, pIDProject, pSampleType = 'ACPT', pSelectedParentLabIDs = undefined, pConciergeConfig = undefined, pLineItemIDs = undefined)
	{
		const tmpIDMaterial = Number(pIDMaterial) > 0 ? Number(pIDMaterial) : 0;
		const tmpLineItemIDs = MaterialsFormManager.normalizeLineItemIDs(pLineItemIDs);
		if (!tmpIDMaterial)
		{
			// Without a material there is nothing to scope the sampling plan or the spec-year requirements by;
			// an empty material filter must not widen to every row on the project.
			this.log.info(`No material given for project ${pIDProject}; no default tests resolved`);
			return { Tests: [], Reason: 'NoMaterial', Diagnostics: { IDProject: pIDProject, IDMaterial: 0, LineItemIDs: tmpLineItemIDs, SampleType: pSampleType, SamplingPlanRowCount: 0 } };
		}
		if (tmpLineItemIDs.length < 1)
		{
			// Both the sampling plan and the spec-year fallback are scoped to the sample's line item, so without
			// one neither can apply — rather than widening to every pay item on the project.
			this.log.info(`No line item given for material ${tmpIDMaterial} in project ${pIDProject}; no default tests resolved`);
			return { Tests: [], Reason: 'NoLineItem', Diagnostics: { IDProject: pIDProject, IDMaterial: tmpIDMaterial, LineItemIDs: [], SampleType: pSampleType, SamplingPlanRowCount: 0 } };
		}
		const tmpLabRoutingConfig = pConciergeConfig && pConciergeConfig.LabRouting;
		const tmpRouter = this.getLabRouter(tmpLabRoutingConfig);
		const tmpAffinityRootIDs = await tmpRouter.getAffinityRootIDs(pIDProject, tmpLabRoutingConfig);
		const tmpProject = await this._get(`Project/${pIDProject}`);
		const tmpContract = tmpProject && tmpProject.IDContract > 0 ? await this._get(`Contract/${tmpProject.IDContract}`) : null;
		if (!tmpContract)
		{
			this.log.warn(`No Contract found for Project ${pIDProject}`);
			return { Tests: [], Reason: 'NoContract', Diagnostics: { IDProject: pIDProject, IDMaterial: pIDMaterial, SampleType: pSampleType, IDContract: (tmpProject && tmpProject.IDContract) || 0 } };
		}
		/*
		const tmpSpecificationSet = tmpContract && tmpContract.ItemIDTestSpecificationSet > 0 ?
			await this._get(`TestSpecificationSet/${tmpContract.ItemIDTestSpecificationSet}`) :
			await this._getAll(`TestSpecificationSets/FilteredTo/FBV~Year~EQ~${tmpContract.ItemSpecificationYear}`).then((res) => res[0]);
		if (!tmpSpecificationSet)
		{
			this.log.warn(`No TestSpecificationSet found for Contract ${tmpContract.IDContract}`);
			return [];
		}
		*/
		//const tmpSpecifications = await this._getAll(`TestSpecifications/FilteredTo/FBV~IDTestSpecificationSet~EQ~${tmpSpecificationSet.IDTestSpecificationSet}`);
		const tmpMaterialIDs = await this._resolveMaterialParents(pIDMaterial);
		//const tmpTestJoins = await this._getAll(`TestSpecificationMaterialTestJoins/FilteredTo/FBL~IDMaterial~INN~${tmpMaterialIDs.join(',')}~FBL~IDTestSpecification~INN~${tmpSpecifications.map((spec) => spec.IDTestSpecification).join(',')}`);
		let tmpLabIDs = pSelectedParentLabIDs;
		let tmpParentLabs = [];
		let tmpChildLabs = [];
		if (Array.isArray(tmpLabIDs) && tmpLabIDs.length > 0)
		{
			[ tmpParentLabs, tmpChildLabs ] = await Promise.all([
				this._getAll(`Labs/FilteredTo/FBL~IDLab~INN~${tmpLabIDs.join(',')}`),
				this._getAll(`Labs/FilteredTo/FBL~IDLabParent~INN~${tmpLabIDs.join(',')}`)
			]);
			this.fable.log.info(`Resolved attached labs with ids: ${ tmpLabIDs.join(',') }`);
		}
		else
		{
			// use ProjectLabJoin if no labs are specified
			const tmpProjectLabsJoins = await this._getAll(`ProjectLabJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}`);
			tmpParentLabs = tmpProjectLabsJoins.length < 1 ? [] : await this._getAll(`Labs/FilteredTo/FBL~IDLab~INN~${tmpProjectLabsJoins.map((pl) => pl.IDLab).join(',')}`);
			tmpChildLabs = tmpProjectLabsJoins.length < 1 ? [] : await this._getAll(`Labs/FilteredTo/FBL~IDLabParent~INN~${tmpProjectLabsJoins.map((pl) => pl.IDLab).join(',')}`);
			tmpLabIDs = Array.from(new Set(tmpParentLabs.map((lab) => lab.IDLab)));
			this.fable.log.info(`Falling back to Project Lab Joins for project ${ pIDProject }, found lab ids: ${ tmpLabIDs.join(',') }`);
		}
		const tmpAllowedLabIDs = await this.getAllowedLabIDsForContract(tmpContract);
		if (tmpAllowedLabIDs)
		{
			// Joined labs sit at either level of the depth-2 lab tree: a child lab is
			// assignable when it or its parent is joined; a parent when it or one of
			// its surviving children is joined.
			tmpChildLabs = tmpChildLabs.filter((pLab) => tmpAllowedLabIDs.has(pLab.IDLab) || tmpAllowedLabIDs.has(pLab.IDLabParent));
			tmpParentLabs = tmpParentLabs.filter((pLab) => tmpAllowedLabIDs.has(pLab.IDLab) || tmpChildLabs.some((pChildLab) => pChildLab.IDLabParent === pLab.IDLab));
			this.fable.log.info(`Assignable labs constrained by the materials-specification-set lab joins for contract ${tmpContract.IDContract}: parent labs [${tmpParentLabs.map((pLab) => pLab.IDLab).join(',')}], child labs [${tmpChildLabs.map((pLab) => pLab.IDLab).join(',')}]`);
		}
		// check for defaults on parent and child labs
		const tmpAllLabs = tmpParentLabs.concat(tmpChildLabs);
		// FIXME: Some of the above code is redundant with this change but we are switching this to use ProjectLineItemMaterialTests. Get this working for now.
		// Sampling-plan rows are scoped to the sample's line item: the same material often appears under
		// several pay items with different tests, and only the sample's own items apply.
		let tmpTestDefaults = await this._getAll(`ProjectLineItemMaterialTests/FilteredTo/FBL~SampleType~INN~${ pSampleType },~FBV~IDProject~EQ~${ pIDProject }~FBL~IDLineItem~INN~${ tmpLineItemIDs.join(',') }~FBL~IDMaterial~INN~${ tmpMaterialIDs.join(',') }`);
		this.fable.log.info(`Found the following PLIMTs for Project ${ pIDProject }, Line Items ${ tmpLineItemIDs.join(',') }, Materials ${ tmpMaterialIDs.join(',') }, Sample Type: ${ pSampleType }: `, { TestDefaults: tmpTestDefaults });
		const tmpSamplingPlanRowCount = tmpTestDefaults.length;
		// Fallback: when the sample's line items have no sampling plan for this material/sample-type,
		// pull tests from the contract's specification year (item spec set), scoped to
		// those line items' pay items. Sample type is filtered in code (a blank requirement type means "any").
		let tmpResolutionSource = 'SamplingPlan';
		let tmpSpecYear = null;
		if (tmpTestDefaults.length === 0)
		{
			tmpSpecYear = await this._resolveSpecYearTestDefaults(pIDMaterial, tmpMaterialIDs, pIDProject, tmpContract, pSampleType, tmpLineItemIDs);
			if (tmpSpecYear.Tests.length > 0)
			{
				tmpTestDefaults = tmpSpecYear.Tests;
				tmpResolutionSource = 'SpecYear';
				this.fable.log.info(`No sampling plan for material ${pIDMaterial} on line item(s) ${tmpLineItemIDs.join(',')} in project ${pIDProject}; using contract spec-year fallback (${tmpTestDefaults.length} test(s))`, { Diagnostics: tmpSpecYear.Diagnostics });
			}
		}

		/* FIXME: this tries to use the test specification to find the tests, but this isn't working...
		const tmpTestDefaults = tmpChildLabs.length < 1 ?
			await this._getAll(`LabTestDefaults/FilteredTo/FBL~SampleType~INN~${pSampleType},~FBL~IDTest~INN~${tmpTestJoins.map((join) => join.IDTest).join(',')}`) :
			await this._getAll(`LabTestDefaults/FilteredTo/FBL~SampleType~INN~${pSampleType},~FBL~IDTest~INN~${tmpTestJoins.map((join) => join.IDTest).join(',')}~FBL~IDLab~INN~${tmpChildLabs.map((pl) => pl.IDLab).join(',')}`);
			*/
		// Commenting this out for now but leaving for reference since plimts won't have IDLab on them.
		/*if (tmpParentLabs.length < 1)
		{
			tmpChildLabs = await this._getAll(`Labs/FilteredTo/FBL~IDLab~INN~${Array.from(new Set(tmpTestDefaults.map((td) => td.IDLab))).join(',')}`);
			tmpParentLabs = tmpChildLabs.length < 1 ? [] : await this._getAll(`Labs/FilteredTo/FBL~IDLab~INN~${Array.from(new Set(tmpChildLabs.map((cl) => cl.IDLabParent))).join(',')}`);
		}*/
		const tmpTestIDs = Array.from(new Set(tmpTestDefaults.map((td) => td.IDTest)));
		const tmpTests = tmpTestIDs.length < 1 ? [] : await this._getAll(`Tests/FilteredTo/FBL~IDTest~INN~${tmpTestIDs.join(',')}`);
		// TestLabJoin is scoped to every in-scope lab, not just the benches: a project lab with no children
		// beneath it IS the bench, and scoping to children alone would leave it with no capability rows.
		const tmpScopeLabIDs = Array.from(new Set(tmpAllLabs.map((pLab) => pLab.IDLab).filter((pIDLab) => pIDLab > 0)));
		const tmpTestLabJoins = (tmpTestIDs.length < 1 || tmpScopeLabIDs.length < 1) ? [] : await this._getAll(`TestLabJoins/FilteredTo/FOP~0~(~0~FBV~DateEnd~GT~${new Date().toISOString()}~FBVOR~DateEnd~EQ~~FCP~0~)~0~FBL~IDTest~INN~${tmpTestIDs.join(',')}~FBL~IDLab~INN~${tmpScopeLabIDs.join(',')}`);
		this.fable.log.info(`Found the following test lab joins for labs ${ tmpScopeLabIDs.join(',') }, Tests ${ tmpTestIDs.join(',') }: `, { TestLabJoins: tmpTestLabJoins });
		// LabTestDefault is the per-test BACK-OFF axis: it narrows a test to its material's usual bench where
		// it has coverage, but it disagrees with TestLabJoin often enough that it is never allowed to empty
		// the candidates. Keyed on material, so it is skipped without one.
		const tmpLabTestDefaultRows = (tmpTestIDs.length < 1 || tmpMaterialIDs.length < 1) ? [] :
			await this._getAll(`LabTestDefaults/FilteredTo/FBL~IDMaterial~INN~${tmpMaterialIDs.join(',')}~FBV~SampleType~EQ~${pSampleType}~FBL~IDTest~INN~${tmpTestIDs.join(',')}`);

		const tmpTestMap = {};
		for (const tmpTest of tmpTests)
		{
			tmpTestMap[tmpTest.IDTest] = tmpTest;
		}
		// A test is run by ONE bench, so the routing below is a partition: every test resolves to exactly one
		// lab and appears in exactly one bucket. Nothing here fans a test out across labs — a sample gets a
		// second copy of a test only when a TestInstance already exists for the second lab.
		await tmpRouter.loadLabCatalog();
		tmpRouter.mergeLabCatalog(tmpAllLabs);
		const tmpTestLabsByTest = this._indexLabIDsByTest(tmpTestLabJoins);
		const tmpTestLabDefaultsByTest = this._indexLabIDsByTest(tmpLabTestDefaultRows);
		const tmpSpecSetFloor = tmpRouter.expandSpecSetFloor(tmpAllowedLabIDs);
		const tmpScopeParentLabIDs = tmpParentLabs.map((pLab) => pLab.IDLab);
		const tmpFirstParentLab = tmpParentLabs[0];
		/** @type {Record<string, number>} */
		const tmpTestLabAssignments = {};
		/** @type {Record<string, string>} */
		const tmpTestLabSources = {};
		for (const tmpIDTest of tmpTestIDs)
		{
			const tmpSelection = tmpRouter.selectTestLabID(tmpIDTest,
				{
					TestLabIDs: tmpTestLabsByTest[String(tmpIDTest)],
					TestLabDefaultIDs: tmpTestLabDefaultsByTest[String(tmpIDTest)],
					ParentLabIDs: tmpScopeParentLabIDs,
					SpecSetFloor: tmpSpecSetFloor,
					AffinityRootIDs: tmpAffinityRootIDs,
				});
			if (tmpSelection.IDLab > 0)
			{
				tmpTestLabAssignments[String(tmpIDTest)] = tmpRouter.applyAggregation(tmpSelection.IDLab);
				tmpTestLabSources[String(tmpIDTest)] = tmpSelection.Source;
				continue;
			}
			// No bench qualified. The sample's own parent lab still routes the test somewhere a technician can
			// see it, which is the behavior that predates per-test resolution.
			if (!tmpFirstParentLab)
			{
				this.fable.log.warn(`Test ID ${tmpIDTest} is not associated with a Lab ID and no parent lab was found; will create tests with no lab for material ${pIDMaterial}`);
				tmpTestLabAssignments[String(tmpIDTest)] = 0;
				tmpTestLabSources[String(tmpIDTest)] = 'None';
				continue;
			}
			tmpTestLabAssignments[String(tmpIDTest)] = tmpFirstParentLab.IDLab;
			tmpTestLabSources[String(tmpIDTest)] = 'SampleParentLab';
		}
		this.fable.log.info(`Resolved test lab assignments for material ${pIDMaterial} in project ${pIDProject}`, { TestLabAssignments: tmpTestLabAssignments, TestLabSources: tmpTestLabSources });
		const tmpTestDefaultsByLab = {};
		for (const tmpTestDefault of tmpTestDefaults)
		{
			const tmpAssignedLabID = tmpTestLabAssignments[String(tmpTestDefault.IDTest)] || 0;
			const tmpLabKey = String(tmpAssignedLabID);
			if (!tmpTestDefaultsByLab[tmpLabKey])
			{
				tmpTestDefaultsByLab[tmpLabKey] = [];
			}
			if (!tmpTestDefaultsByLab[tmpLabKey].find((t) => t.IDTest == tmpTestDefault.IDTest))
			{
				tmpTestDefaultsByLab[tmpLabKey].push(tmpTestDefault);
			}
		}
		const tmpRNIs = await this._getAvailableRNIs(pIDProject);
		// organize RNIs by tests
		let tmpResults = [];
		for (const [ tmpIDLab, tmpTestDefaults ] of Object.entries(tmpTestDefaultsByLab))
		{
			for (const tmpTestDefault of tmpTestDefaults)
			{
				const tmpTestID = tmpTestDefault.IDTest;
				const tmpTest = tmpTestMap[tmpTestID];
				if (!tmpTest)
				{
					this.fable.log.warn(`No test record found for default test ID ${tmpTestID} for material ${pIDMaterial}`);
					continue;
				}
				const tmpLabID = Number(tmpIDLab);
				const tmpLabName = tmpIDLab == '0' ? '' : (tmpAllLabs.find((lab) => lab.IDLab === tmpLabID) || tmpRouter.getLab(tmpLabID) || {}).Name || `Lab ${tmpIDLab}`;
				const tmpRNI = tmpRNIs.find((rni) => rni.Options.TestHash === tmpTest.Hash);
				const tmpFallbackRNI = tmpRNIs.find((rni) => Array.isArray(rni.Options.TestHashes) && rni.Options.TestHashes.includes(tmpTest.Hash));
				const tmpMatchedRNI = tmpRNI || tmpFallbackRNI;
				if (tmpMatchedRNI)
				{
					// Check if we already have a result for this RNI + lab combination
					const tmpExistingResult = tmpResults.find((r) => r.IDReportNamedInstance === tmpMatchedRNI.IDReportNamedInstance && r.LabID === tmpLabID);
					if (tmpExistingResult)
					{
						this.fable.log.info(`Form ${ tmpMatchedRNI.Name } for test ID ${tmpTestID} (hash: ${tmpTest.Hash}) already in result set.`);
						// Accumulate this test into the existing result
						if (!tmpExistingResult.SatisfiedTestHashes.includes(tmpTest.Hash))
						{
							tmpExistingResult.SatisfiedTestHashes.push(tmpTest.Hash);
						}
						if (!tmpExistingResult.SatisfiedIDTests.includes(tmpTestID))
						{
							tmpExistingResult.SatisfiedIDTests.push(tmpTestID);
						}
						continue;
					}
					this.fable.log.info(`Adding Form ${ tmpMatchedRNI.Name } for test ID ${tmpTestID} (hash: ${tmpTest.Hash}) to result set.`);

					const tmpResult = Object.assign({}, tmpMatchedRNI);
					tmpResult.Enabled = true;
					tmpResult.DisplayName = `Default Test RNI: ${tmpMatchedRNI.Name}`;
					tmpResult.TestHashes = tmpMatchedRNI.Options.TestHashes;
					tmpResult.TestHash = tmpRNI ? tmpRNI.Options.TestHash : tmpTest.Hash;
					tmpResult.IDTest = tmpTestID;
					tmpResult.SatisfiedIDTests = [tmpTestID];
					tmpResult.SatisfiedTestHashes = [tmpTest.Hash];
					tmpResult.LabID = tmpLabID;
					tmpResult.LabName = tmpLabName;
					tmpResult.LineItemIDs = tmpTestDefault.IDLineItem ? [ tmpTestDefault.IDLineItem ] : [];
					tmpResults.push(tmpResult);
					continue;
				}
				// Use the join-aware resolver — checks TestManifestSchemaJoin first then falls back
				// to Hash-based lookup. tmpTest already has IDTest/IDCustomer/Hash so no refetch.
				const tmpInjectableManifest = await this._resolveManifestForTestRecord(tmpTest);
				if (tmpInjectableManifest)
				{
					this.fable.log.info(`Found injectable Manifest with ID ${ tmpInjectableManifest.IDManifestSchema } for test ID ${tmpTestID} (hash: ${tmpTest.Hash}), adding to result set.`);
					tmpInjectableManifest.Enabled = true;
					tmpInjectableManifest.IDTest = tmpTestID;
					tmpInjectableManifest.SatisfiedIDTests = [tmpTestID];
					tmpInjectableManifest.SatisfiedTestHashes = [tmpTest.Hash];
					tmpInjectableManifest.LabID = tmpLabID;
					tmpInjectableManifest.LabName = tmpLabName;
					tmpInjectableManifest.Name = tmpInjectableManifest.Name || tmpTest.Name || tmpTest.Hash;
					tmpInjectableManifest.LineItemIDs = tmpTestDefault.IDLineItem ? [ tmpTestDefault.IDLineItem ] : [];
					tmpResults.push(tmpInjectableManifest);
					continue;
				}
				this.fable.log.warn(`No RNI or manifest found for default test ID ${tmpTestID} (hash: ${tmpTest.Hash}) for material ${pIDMaterial}`, { tmpTest });
				const tmpMissingTestEntry = Object.assign({}, tmpTest,
				{
					Enabled: true,
					IDTest: tmpTestID,
					SatisfiedIDTests: [tmpTestID],
					SatisfiedTestHashes: [tmpTest.Hash],
					LabID: tmpLabID,
					LabName: tmpLabName,
					DisplayName: tmpTest.Name || tmpTest.Hash,
					Missing: true,
					TestHash: tmpTest.Hash,
					LineItemIDs: tmpTestDefault.IDLineItem ? [ tmpTestDefault.IDLineItem ] : [],
				});
				tmpResults.push(tmpMissingTestEntry);
			}
		}
		let tmpReason = 'OK';
		if (tmpResults.length === 0)
		{
			if (tmpTestDefaults.length > 0)
			{
				tmpReason = 'TestsUnresolved';
			}
			else if (tmpSpecYear)
			{
				// The sampling plan was empty AND the spec-year fallback found nothing —
				// surface the fallback's specific reason (NoSpecYear / NoMatchingPayItems /
				// MaterialNotInPayItems / NoSpecYearTests).
				tmpReason = tmpSpecYear.Reason;
			}
			else
			{
				tmpReason = 'NoSamplingPlan';
			}
		}
		return {
			Tests: tmpResults,
			Reason: tmpReason,
			Diagnostics:
			{
				IDProject: pIDProject,
				IDMaterial: pIDMaterial,
				LineItemIDs: tmpLineItemIDs,
				SampleType: pSampleType,
				IDContract: tmpContract.IDContract,
				ItemSpecificationYear: tmpContract.ItemSpecificationYear,
				ResolvedMaterialIDs: tmpMaterialIDs,
				Source: tmpResolutionSource,
				SamplingPlanRowCount: tmpSamplingPlanRowCount,
				ResolvedTestCount: tmpResults.length,
				MissingFormCount: tmpResults.filter((r) => r.Missing).length,
				ResolvedLabIDs: Object.keys(tmpTestDefaultsByLab),
				TestLabAssignments: tmpTestLabAssignments,
				TestLabSources: tmpTestLabSources,
				AffinityLabIDs: tmpAffinityRootIDs,
				AssignableLabScope: tmpAllowedLabIDs ? { AllowedLabCount: tmpAllowedLabIDs.size, ParentLabIDs: tmpParentLabs.map((pLab) => pLab.IDLab), ChildLabIDs: tmpChildLabs.map((pLab) => pLab.IDLab) } : null,
				SpecYear: tmpSpecYear ? tmpSpecYear.Diagnostics : null,
			},
		};
	}

	/**
	 * Fallback test resolution from the contract's specification year, used when the
	 * sample's line items have no sampling plan (ProjectLineItemMaterialTest) for a material.
	 * Reads the spec-year requirements (PayItemMaterialTestRequirement — the
	 * pay-item / material / test / sample-type shape) and HARD-GATES on the
	 * sample's line items: a requirement is honored only when its pay item is one of
	 * those line items' pay items (matched by IDPayItem, or by ItemCode ->
	 * PayItem.ItemCode within the contract's spec set). Sample type is filtered
	 * against the sample's type (a blank requirement SampleType is treated as
	 * "any"). Returns the same `{ IDTest, IDMaterial }` shape the PLIMT branch
	 * produces so the downstream resolution is identical.
	 *
	 * @param {number} pIDMaterial - the sample's material
	 * @param {Array<number>} pMaterialIDs - the material and its parent chain
	 * @param {number} pIDProject
	 * @param {Record<string, any>} pContract - the project's contract
	 * @param {string} pSampleType - the sample's type to filter requirements by
	 * @param {Array<number>} pLineItemIDs - the sample's line items; only their pay items are honored
	 * @return {Promise<{ Tests: Array<Record<string, any>>, Reason: string, Diagnostics: Record<string, any> }>}
	 *   Reason: 'OK' | 'NoSpecYear' | 'NoMatchingPayItems' | 'MaterialNotInPayItems' | 'NoSpecYearTests'
	 */
	async _resolveSpecYearTestDefaults(pIDMaterial, pMaterialIDs, pIDProject, pContract, pSampleType, pLineItemIDs)
	{
		const tmpLineItemIDs = MaterialsFormManager.normalizeLineItemIDs(pLineItemIDs);
		const tmpDiagnostics = { IDProject: pIDProject, IDMaterial: pIDMaterial, LineItemIDs: tmpLineItemIDs, SampleType: pSampleType };
		// 1. Resolve the contract's ITEM specification set (explicit id, else by year).
		let tmpSpecSetID = pContract.ItemIDTestSpecificationSet > 0 ? pContract.ItemIDTestSpecificationSet : 0;
		if (!tmpSpecSetID && pContract.ItemSpecificationYear)
		{
			// See _resolveMaterialSpecificationSetID: a year can match a provisional set as well as the live one.
			const tmpSets = await this._getAll(`TestSpecificationSets/FilteredTo/FBV~IDCustomer~EQ~${pContract.IDCustomer}~FBV~Year~EQ~${encodeURIComponent(pContract.ItemSpecificationYear)}~FSF~IDTestSpecificationSet~ASC~0`);
			if (tmpSets.length > 1)
			{
				this.fable.log.warn(`Item specification year ${pContract.ItemSpecificationYear} matches ${tmpSets.length} specification sets for customer ${pContract.IDCustomer}; using the lowest id (${tmpSets[0].IDTestSpecificationSet}). Set the contract's ItemIDTestSpecificationSet to resolve this explicitly.`);
			}
			tmpSpecSetID = tmpSets.length > 0 ? tmpSets[0].IDTestSpecificationSet : 0;
		}
		tmpDiagnostics.IDTestSpecificationSet = tmpSpecSetID;
		if (!tmpSpecSetID)
		{
			return { Tests: [], Reason: 'NoSpecYear', Diagnostics: tmpDiagnostics };
		}
		// 2. The sample's line items (on this project) give the pay items, matched to the
		//    set's pay items by IDPayItem or ItemCode. Without a line item nothing is in scope.
		const tmpLineItems = tmpLineItemIDs.length > 0 ? await this._getAll(`LineItems/FilteredTo/FBV~IDProject~EQ~${pIDProject}~FBL~IDLineItem~INN~${tmpLineItemIDs.join(',')}`) : [];
		const tmpLineItemPayItemIDs = new Set(tmpLineItems.map((li) => li.IDPayItem).filter((id) => id > 0));
		const tmpLineItemCodes = new Set(tmpLineItems.map((li) => li.ItemCode).filter((c) => c != null && String(c).length > 0));
		tmpDiagnostics.ProjectLineItemCount = tmpLineItems.length;
		const tmpSetPayItems = await this._getAll(`PayItems/FilteredTo/FBV~IDTestSpecificationSet~EQ~${tmpSpecSetID}`);
		const tmpScopedPayItemIDs = new Set(tmpSetPayItems
			.filter((pi) => tmpLineItemPayItemIDs.has(pi.IDPayItem) || tmpLineItemCodes.has(pi.ItemCode))
			.map((pi) => pi.IDPayItem));
		tmpDiagnostics.MatchedPayItemCount = tmpScopedPayItemIDs.size;
		if (tmpScopedPayItemIDs.size === 0)
		{
			return { Tests: [], Reason: 'NoMatchingPayItems', Diagnostics: tmpDiagnostics };
		}
		// 3. Spec-year requirements for the material (+ parents). Query by material
		//    (tiny list), then HARD-GATE to the line items' matched pay items in code.
		const tmpRequirements = await this._getAll(`PayItemMaterialTestRequirements/FilteredTo/FBL~IDMaterial~INN~${pMaterialIDs.join(',')}`);
		const tmpGatedRequirements = tmpRequirements.filter((r) => tmpScopedPayItemIDs.has(r.IDPayItem));
		tmpDiagnostics.PayItemGatedRequirementCount = tmpGatedRequirements.length;
		if (tmpGatedRequirements.length === 0)
		{
			return { Tests: [], Reason: 'MaterialNotInPayItems', Diagnostics: tmpDiagnostics };
		}
		// 4. Filter by sample type (a blank requirement SampleType means "any type"),
		//    then collapse to unique IDTest in the PLIMT-compatible shape.
		const tmpWantType = String(pSampleType || '').trim().toUpperCase();
		const tmpTests = [];
		const tmpSeenTestIDs = new Set();
		for (const tmpRequirement of tmpGatedRequirements)
		{
			const tmpRowType = String(tmpRequirement.SampleType || '').trim().toUpperCase();
			if (tmpRowType && tmpWantType && tmpRowType !== tmpWantType)
			{
				continue;
			}
			if (tmpRequirement.IDTest > 0 && !tmpSeenTestIDs.has(tmpRequirement.IDTest))
			{
				tmpSeenTestIDs.add(tmpRequirement.IDTest);
				tmpTests.push({ IDTest: tmpRequirement.IDTest, IDMaterial: tmpRequirement.IDMaterial, SampleType: tmpRequirement.SampleType, Source: 'SpecYear' });
			}
		}
		tmpDiagnostics.SpecYearTestCount = tmpTests.length;
		return { Tests: tmpTests, Reason: tmpTests.length > 0 ? 'OK' : 'NoSpecYearTests', Diagnostics: tmpDiagnostics };
	}

	async _getRNIForDocumentRecord(pDocumentRecord)
	{
		return (await this._getAvailableRNIs(pDocumentRecord.IDProject))?.find?.((rni) => rni.Hash === pDocumentRecord.DocumentType);
	}

	/**
	 * @param {Record<string, any>} pDocumentRecord
	 *
	 * @return {Promise<Record<string, any>>}
	 */
	async _getManifestForDocumentRecord(pDocumentRecord)
	{
		//FIXME: ...
		const tmpRNI = await this._getRNIForDocumentRecord(pDocumentRecord);
		if (!tmpRNI)
		{
			this.fable.log.warn(`No RNI found for document record`, { pDocumentRecord });
			return null;
		}
		return await this._loadFormManifest(tmpRNI);
	}

	/**
	 * Fetch the form manifest, if configured.
	 *
	 * NOTE: Mirrors the logic in report_definitions/global/server_js/mixins/document.pre-render-mixins.js#LoadFormManifest
	 *
	 * @param {any} pRNI - the ReportNamedInstance
	 *
	 * @return {Promise<Record<string, any>>} the form manifest object, or null
	 */
	async _loadFormManifest(pRNI)
	{
		const reportOptions = pRNI.Options;
		if (typeof reportOptions.FormDefinition === 'object')
		{
			return reportOptions.FormDefinition.DefaultFormManifest;
		}
		try
		{
			if (reportOptions.FormManifestSchemaID > 0)
			{
				return this.getManifestByID(reportOptions.FormManifestSchemaID).then((pManifestSchema) => pManifestSchema.Manifest);
			}
			if (reportOptions.FormManifestSchemaHash)
			{
				return this.getManifestByHash(reportOptions.FormManifestSchemaHash, pRNI.IDCustomer);
			}
		}
		catch (error)
		{
			this.fable.log.error(`Error loading form manifest: ${error}`);
		}
		return null;
	};

	/**
	 * Seed the sample-level Lab selector on a generated document.
	 *
	 * The Concierge resolves the sample's labs server-side, but a generated document is never driven
	 * through the interactive sample-selection chain that would otherwise derive them, so the Labs
	 * field renders empty. Writes the lab ids straight to the descriptor's address instead.
	 *
	 * The value is a comma-joined list of IDLab — the shape the Multiple entity selector parses
	 * (PictProvider-InputType-MaterialsEntitySelector splits a string value on commas). Per-test lab
	 * inputs (MaterialsTestLab*) are deliberately not matched; this is the sample-level field only.
	 *
	 * @param {Record<string, any>} pDocumentRecord - The document to seed (mutated in place).
	 * @param {Record<string, any>} pDocumentManifest - The already-resolved manifest for the document.
	 * @param {Array<number>} pLabIDs - The resolved lab ids for the sample.
	 *
	 * @return {string} the address written, or '' when the form has no lab selector
	 */
	_setSampleLabsInDocument(pDocumentRecord, pDocumentManifest, pLabIDs)
	{
		if (!Array.isArray(pLabIDs) || pLabIDs.length < 1 || !pDocumentManifest)
		{
			return '';
		}
		let tmpLabDescriptor;
		let tmpLabAddress = '';
		for (const [ tmpKey, tmpDescriptor ] of Object.entries(pDocumentManifest.Descriptors || {}))
		{
			if (LAB_SELECTOR_INPUT_TYPES.indexOf(tmpDescriptor.PictForm?.InputType) < 0)
			{
				continue;
			}
			if (tmpLabDescriptor)
			{
				this.fable.log.warn(`Multiple sample lab selectors found in document manifest; seeding the first ('${tmpLabAddress}') and ignoring '${tmpDescriptor.DataAddress || tmpKey}'.`);
				continue;
			}
			tmpLabDescriptor = tmpDescriptor;
			tmpLabAddress = tmpDescriptor.DataAddress || tmpKey;
		}
		if (!tmpLabDescriptor)
		{
			// Not every generated form carries a Labs field; nothing to seed.
			return '';
		}
		const tmpUniqueLabIDs = Array.from(new Set(pLabIDs.map((pIDLab) => Number(pIDLab)).filter((pIDLab) => pIDLab > 0)));
		if (tmpUniqueLabIDs.length < 1)
		{
			return '';
		}
		this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpLabAddress, tmpUniqueLabIDs.join(','));
		return tmpLabAddress;
	}

	/**
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {string} [pGUIDSample]
	 * @param {Array<number>} [pLabIDs] - resolved lab ids for the sample, seeded into the form's Labs field
	 *
	 * @return {Promise<string>}
	 */
	async setSampleIdentityInDocument(pDocumentRecord, pGUIDSample, pLabIDs)
	{
		if (!pDocumentRecord)
		{
			throw new Error(`No document record provided to setSampleIdentityInDocument`);
		}
		if (!pDocumentRecord.GUIDDocument)
		{
			pDocumentRecord.GUIDDocument = this.fable.getUUID();
		}
		if (!pDocumentRecord.FormData)
		{
			pDocumentRecord.FormData = {};
		}
		if (!pDocumentRecord.FormData.__DynamicSectionData)
		{
			pDocumentRecord.FormData.__DynamicSectionData = {};
		}
		if (!Array.isArray(pDocumentRecord.FormData.__DynamicSectionData.Sequence))
		{
			pDocumentRecord.FormData.__DynamicSectionData.Sequence = [];
		}
		const tmpDocumentManifest = await this._getManifestForDocumentRecord(pDocumentRecord);
		if (pGUIDSample != null)
		{
			let tmpSampleSelectorDescriptor;
			if (tmpDocumentManifest)
			{
				for (const [ tmpKey, tmpDescriptor ] of Object.entries(tmpDocumentManifest.Descriptors || {}))
				{
					if (tmpDescriptor.PictForm?.InputType === 'SampleSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsSampleSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsHiddenSampleSelector')
					{
						tmpSampleSelectorDescriptor = tmpDescriptor;
						if (!tmpSampleSelectorDescriptor.DataAddress)
						{
							tmpSampleSelectorDescriptor.DataAddress = tmpKey;
						}
					}
				}
				if (!tmpSampleSelectorDescriptor)
				{
					this.fable.log.warn(`No Sample Selector found in document manifest; defaulting to 'SampleID' address.`);
				}
			}
			else
			{
				this.fable.log.warn(`No document manifest found; defaulting to 'SampleID' address.`);
			}
			const tmpSampleAddress = tmpSampleSelectorDescriptor ? tmpSampleSelectorDescriptor.DataAddress : 'SampleID';
			this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpSampleAddress, String(pGUIDSample));
			const tmpSampleSaltDataAddress = `${tmpSampleAddress}__Salt`;

			let tmpSampleSalt = this.fable.Utility.getValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress);
			if (!tmpSampleSalt)
			{
				tmpSampleSalt = this._generateSaltStr();
				this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress, tmpSampleSalt);
			}
			const tmpLabAddress = this._setSampleLabsInDocument(pDocumentRecord, tmpDocumentManifest, pLabIDs);
			if (tmpLabAddress)
			{
				this.fable.log.info(`Seeded ${tmpLabAddress} on document ${pDocumentRecord.GUIDDocument} with labs [${pLabIDs.join(',')}].`);
			}
			return tmpSampleSalt;
		}
		return null;
	}

	/**
	 * NOTE: does not save document
	 *
	 * @param {Array<Record<string, any>>} pTests - tests retrieved from getMaterialTestDefaults with Enabled flags set as desired
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {string} [pGUIDSample]
	 *
	 * @return {Promise<Record<string, Record<string, Record<string, any>>>>} - The updated document record with tests applied.
	 */
	async applyMaterialTestsToDocumentRecord(pTests, pDocumentRecord, pGUIDSample)
	{
		if (!pDocumentRecord)
		{
			throw new Error(`No document record provided to applyMaterialTestsToDocumentRecord`);
		}
		if (!pDocumentRecord.GUIDDocument)
		{
			pDocumentRecord.GUIDDocument = this.fable.getUUID();
		}
		if (!pDocumentRecord.FormData)
		{
			pDocumentRecord.FormData = {};
		}
		if (!pDocumentRecord.FormData.__DynamicSectionData)
		{
			pDocumentRecord.FormData.__DynamicSectionData = {};
		}
		if (!Array.isArray(pDocumentRecord.FormData.__DynamicSectionData.Sequence))
		{
			pDocumentRecord.FormData.__DynamicSectionData.Sequence = [];
		}
		const tmpDocumentManifest = await this._getManifestForDocumentRecord(pDocumentRecord);
		const tmpTestInputMap = { };
		if (pGUIDSample != null)
		{
			let tmpSampleSelectorDescriptor;
			if (tmpDocumentManifest)
			{
				for (const [ tmpKey, tmpDescriptor ] of Object.entries(tmpDocumentManifest.Descriptors || {}))
				{
					if (tmpDescriptor.PictForm?.InputType === 'SampleSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsSampleSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsHiddenSampleSelector')
					{
						tmpSampleSelectorDescriptor = tmpDescriptor;
						if (!tmpSampleSelectorDescriptor.DataAddress)
						{
							tmpSampleSelectorDescriptor.DataAddress = tmpKey;
						}
					}
					if (tmpDescriptor.PictForm?.InputType === 'TestSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsTestSelector' ||
						tmpDescriptor.PictForm?.InputType === 'MaterialsHiddenTestSelector')
					{
						if (tmpDescriptor.Default)
						{
							tmpTestInputMap[tmpDescriptor.Default] = tmpDescriptor;
						}
					}
				}
				if (!tmpSampleSelectorDescriptor)
				{
					this.fable.log.warn(`No Sample Selector found in document manifest; defaulting to 'SampleID' address.`);
				}
			}
			else
			{
				this.fable.log.warn(`No document manifest found; defaulting to 'SampleID' address.`);
			}
			const tmpSampleAddress = tmpSampleSelectorDescriptor ? tmpSampleSelectorDescriptor.DataAddress : 'SampleID';
			this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpSampleAddress, String(pGUIDSample));
		}
		const tmpDynamicSectionData = pDocumentRecord.FormData.__DynamicSectionData;
		const tmpTestRecordComprehension =
		{
			TestInstance: { }, TestInstanceSampleJoin: { }, DocumentPolyJoin: { },
			Document:
			{
				[`${ pDocumentRecord.GUIDDocument }`]: { GUIDDocument: pDocumentRecord.GUIDDocument, IDProject: pDocumentRecord.IDProject }
			}
		};
		let tmpTestDescriptor;
		let tmpSampleDescriptor;
		if (tmpDocumentManifest)
		{
			for (const [ tmpKey, tmpDescriptor ] of Object.entries(tmpDocumentManifest.Descriptors || {}))
			{
				if (tmpDescriptor.PictForm?.InputType === 'MaterialsTestSelector' ||
					tmpDescriptor.PictForm?.InputType === 'MaterialsHiddenTestSelector')
				{
					tmpTestDescriptor = tmpDescriptor;
					if (!tmpTestDescriptor.DataAddress)
					{
						tmpTestDescriptor.DataAddress = tmpKey;
					}
					break;
				}
				if (tmpDescriptor.PictForm?.InputType === 'MaterialsSampleSelector' ||
					tmpDescriptor.PictForm?.InputType === 'MaterialsHiddenSampleSelector')
				{
					tmpSampleDescriptor = tmpDescriptor;
					if (!tmpSampleDescriptor.DataAddress)
					{
						tmpSampleDescriptor.DataAddress = tmpKey;
					}
					break;
				}
			}
		}
		this._addSampleToComprehension(pGUIDSample, pDocumentRecord, tmpSampleDescriptor, tmpTestRecordComprehension);
		for (const tmpTest of pTests)
		{
			if (!tmpTest.Enabled)
			{
				continue;
			}
			if (tmpTest.IDReportNamedInstance)
			{
				// we can't apply an RNI to an existing document; will be handled externally
				const tmpSatisfiedTestHashes = tmpTest.SatisfiedTestHashes || tmpTest.TestHashes || [];
				const tmpSatisfiedIDTests = tmpTest.SatisfiedIDTests || [];
				for (let tmpIdx = 0; tmpIdx < tmpSatisfiedTestHashes.length; tmpIdx++)
				{
					const tmpTestHash = tmpSatisfiedTestHashes[tmpIdx];
					const tmpTestDescriptor = tmpTestInputMap[tmpTestHash];
					if (!tmpTestDescriptor)
					{
						this.fable.log.error(`No test selector for test hash ${tmpTestHash} in the ${pDocumentRecord.DocumentType} manifest; its TestInstance is not linked to this document`, { IDReportNamedInstance: tmpTest.IDReportNamedInstance, TestHash: tmpTestHash });
						continue;
					}
					const tmpTestDataAddress = tmpTestDescriptor?.DataAddress;
					if (!tmpTestDataAddress)
					{
						this.fable.log.error(`No data address on the test selector for test hash ${tmpTestHash} in the ${pDocumentRecord.DocumentType} manifest; its TestInstance is not linked to this document`, { IDReportNamedInstance: tmpTest.IDReportNamedInstance, TestHash: tmpTestHash });
						continue;
					}
					// Reuse the existing salt if the document already has one for this
					// test slot, so the same TestInstance GUID is produced and the
					// comprehension upserts the existing record rather than creating a
					// duplicate.
					const tmpSampleSaltDataAddress = `${tmpTestDataAddress}__Salt`;
					let tmpSalt = this.fable.Utility.getValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress);
					if (!tmpSalt)
					{
						tmpSalt = this._generateSaltStr();
						this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress, tmpSalt);
					}
					const tmpHash = `${tmpSalt}_${tmpTestHash}`;
					// Standalone-form path: the test binds to a flat decorated selector whose DataAddress IS the record
					// base (the *-ladotd manifests carry only String selectors, no record/container address). Write flat
					// so the form reads the same salt and adopts the TestInstance; only the injectable dynamic-section
					// path below nests under <salt>_<testHash>.
					// Resolve the correct IDTest for this specific test hash
					const tmpIDTest = tmpSatisfiedIDTests[tmpIdx] || tmpTest.IDTest;
					const tmpTestInstanceForHash = tmpTest.TestInstance
						? Object.assign({}, tmpTest.TestInstance, { IDTest: tmpIDTest })
						: { IDTest: tmpIDTest };
					await this._addTestToComprehension({ TestHash: tmpTestHash, IDTest: tmpIDTest, TestInstance: tmpTestInstanceForHash }, pGUIDSample, { Hash: tmpHash, Flat: true }, pDocumentRecord, tmpTestDescriptor, tmpTestRecordComprehension);
				}
				continue;
			}
			if (tmpTest.IDManifestSchema || tmpTest.TestHash)
			{
				const tmpSalt = this._generateSaltStr();
				let tmpHash;
				if (tmpTest.TestHash)
				{
					// add manifest schema to document
					//FIXME: currently we are just adding the test hash, rework to add arbitrary manifest schemas?
					tmpHash = `${tmpSalt}_${tmpTest.TestHash}`;
					tmpDynamicSectionData.Sequence.push(tmpHash);
					tmpDynamicSectionData[tmpHash] =
					{
						Hash: tmpHash,
						SaltHash: tmpSalt,
						TestHash: tmpTest.TestHash,
					};
				}
				else
				{
					tmpHash = tmpSalt;
					tmpDynamicSectionData.Sequence.push(tmpHash);
					tmpDynamicSectionData[tmpHash] =
					{
						Hash: tmpHash,
						SaltHash: tmpSalt,
						IDManifestSchema: tmpTest.IDManifestSchema,
					};
				}
				await this._addTestToComprehension(tmpTest, pGUIDSample, tmpDynamicSectionData[tmpHash], pDocumentRecord, tmpTestDescriptor, tmpTestRecordComprehension);
			}
			else
			{
				this.fable.log.warn(`Unknown test type encountered in applyMaterialTestsToDocumentRecord`, { tmpTest });
			}
		}
		return tmpTestRecordComprehension;
	}

	/**
	 * @param {string} pGUIDSample
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {Record<string, any>} pSampleDescriptor
	 * @param {Record<string, Record<string, any>>} pTestRecordComprehension
	 */
	_addSampleToComprehension(pGUIDSample, pDocumentRecord, pSampleDescriptor, pTestRecordComprehension)
	{
		//TODO: support for multi-sample forms?
		const tmpSampleDataAddress = pSampleDescriptor?.DataAddress || 'SampleID';
		const tmpSampleSaltDataAddress = `${tmpSampleDataAddress}__Salt`;

		let tmpSampleSalt = this.fable.Utility.getValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress);
		if (!tmpSampleSalt)
		{
			tmpSampleSalt = this._generateSaltStr();
			this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpSampleSaltDataAddress, tmpSampleSalt);
		}
		const tmpDocumentPolyJoin =
		{
			GUIDDocumentPolyJoin: `${pDocumentRecord.GUIDDocument}-S_DPJ-${tmpSampleSalt}`,
			GUIDDocument: pDocumentRecord.GUIDDocument,
			RemoteType: 'Sample',
			_GUIDSample: pGUIDSample,
			_Dest__GUIDSample: 'IDRemote',
		};
		if (pDocumentRecord.IDDocument > 0)
		{
			tmpDocumentPolyJoin.IDDocument = pDocumentRecord.IDDocument;
		}
		else
		{
			tmpDocumentPolyJoin.GUIDDocument = pDocumentRecord.GUIDDocument;
		}
		pTestRecordComprehension.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
	}

	/**
	 * The write a record needs. A record that does not exist yet is written whole. An existing record
	 * may have been edited by someone else since it was read, so it carries only its identity and the
	 * fields that differ from the row as read — or nothing at all when no field does.
	 *
	 * @param {string} pEntity - The entity name, e.g. 'TestInstance'.
	 * @param {Record<string, any>} pRecordAsRead - A copy of the record taken before any change was applied.
	 * @param {Record<string, any>} pRecord - The record as it stands now.
	 *
	 * @return {Record<string, any> | null} the record to write, or null when there is nothing to write
	 */
	_getRecordWrite(pEntity, pRecordAsRead, pRecord)
	{
		const tmpIDField = `ID${pEntity}`;
		const tmpGUIDField = `GUID${pEntity}`;
		if (!(pRecord[tmpIDField] > 0))
		{
			return pRecord;
		}
		/** @type {Record<string, any>} */
		const tmpWrite = { [tmpIDField]: pRecord[tmpIDField] };
		let tmpHasChange = false;
		for (const [ tmpField, tmpValue ] of Object.entries(pRecord))
		{
			if (tmpField === tmpIDField || pRecordAsRead[tmpField] === tmpValue)
			{
				continue;
			}
			tmpWrite[tmpField] = tmpValue;
			tmpHasChange = true;
		}
		if (!tmpHasChange)
		{
			return null;
		}
		if (pRecord[tmpGUIDField])
		{
			tmpWrite[tmpGUIDField] = pRecord[tmpGUIDField];
		}
		return tmpWrite;
	}

	/**
	 * @param {Record<string, any>} pTest
	 * @param {string} pGUIDSample
	 * @param {Record<string, any>} pUniqueTestData
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {Record<string, any>} pTestDescriptor
	 * @param {Record<string, Record<string, any>>} pTestRecordComprehension
	 *
	 * @return {Promise<void>}
	 */
	async _addTestToComprehension(pTest, pGUIDSample, pUniqueTestData, pDocumentRecord, pTestDescriptor, pTestRecordComprehension)
	{
		if (!pGUIDSample)
		{
			this.fable.log.error(`No GUIDSample provided to _addTestToComprehension`, { pTest, pUniqueTestData, pDocumentRecord, pTestDescriptor });
			return;
		}
		const tmpTestDataAddressBase = pTestDescriptor?.DataAddress || 'TestID';
		// Injectable tests live in a dynamic section keyed <salt>_<testHash>; a standalone form binds the test to a
		// flat decorated selector whose DataAddress IS the record base. Nesting a standalone record under the
		// section key hides it from the flat selector, which then mints a second TestInstance on save (duplicate).
		const tmpTestDataAddress = pUniqueTestData.Flat ? tmpTestDataAddressBase : `${pUniqueTestData.Hash}.${tmpTestDataAddressBase}`;
		const tmpTestSaltDataAddress = `${tmpTestDataAddress}__Salt`;
		const tmpTestInstanceDataAddress = `${tmpTestDataAddress}__Record_Instance`;
		const tmpTestInstanceSampleJoinDataAddress = `${tmpTestDataAddress}__Record_SampleJoin`;
		const tmpDocumentPolyJoinDataAddress = `${tmpTestDataAddress}__Record_DocumentPolyJoin`;

		let tmpTestSalt = this.fable.Utility.getValueByHash(pDocumentRecord.FormData, tmpTestSaltDataAddress);
		if (!tmpTestSalt)
		{
			tmpTestSalt = this._generateSaltStr();
			this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpTestSaltDataAddress, tmpTestSalt);
		}
		let tmpTestInstance = pTest.TestInstance
			|| this.fable.Utility.getValueByHash(pDocumentRecord.FormData, tmpTestInstanceDataAddress)
			|| { };
		const tmpTestInstanceAsRead = Object.assign({ }, tmpTestInstance);
		if (!tmpTestInstance.GUIDTestInstance)
		{
			tmpTestInstance.GUIDTestInstance = `${pDocumentRecord.GUIDDocument}-TI-${tmpTestSalt}`;
		}
		if (!tmpTestInstance.IDTest)
		{
			tmpTestInstance.IDTest = pTest.IDTest || (await this._getTestByHash(pTest.TestHash))?.IDTest || 0;
		}
		if (!tmpTestInstance.IDTest)
		{
			this.fable.log.error(`Could not resolve IDTest for test in applyMaterialTestsToDocumentRecord`, { pTest });
			return;
		}
		if (!(tmpTestInstance.IDTestInstance > 0) && !tmpTestInstance.Name && pTest.TestHash)
		{
			tmpTestInstance.Name = `Sample Test for ${pTest.TestHash}`;
		}
		this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpTestInstanceDataAddress, tmpTestInstance);
		let tmpTestInstanceSampleJoin;
		if (tmpTestInstance.IDTestInstance > 0)
		{
			const tmpSample = await this._get(`Samples/FilteredTo/FBV~GUIDSample~EQ~${encodeURIComponent(pGUIDSample)}`).then((res) => res[0]);
			if (tmpSample)
			{
				tmpTestInstanceSampleJoin = await this._get(`TestInstanceSampleJoins/FilteredTo/FBV~IDTestInstance~EQ~${tmpTestInstance.IDTestInstance}~FBV~IDSample~EQ~${tmpSample.IDSample}`).then((res) => res[0]);
			}
		}
		if (!tmpTestInstanceSampleJoin)
		{
			tmpTestInstanceSampleJoin =
			{
				GUIDTestInstanceSampleJoin: `${pDocumentRecord.GUIDDocument}-TISJ-${tmpTestSalt}`,
				GUIDTestInstance: tmpTestInstance.GUIDTestInstance,
				_GUIDSample: pGUIDSample,
			};
		}
		this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpTestInstanceSampleJoinDataAddress, tmpTestInstanceSampleJoin);
		const tmpDocumentPolyJoin =
		{
			GUIDDocumentPolyJoin: `${pDocumentRecord.GUIDDocument}-TI_DPJ-${tmpTestSalt}`,
			RemoteType: 'TestInstance',
			GUIDDocument: pDocumentRecord.GUIDDocument,
			GUIDTestInstance: tmpTestInstance.GUIDTestInstance,
			_Dest_GUIDTestInstance: 'IDRemote',

		};
		this.fable.Utility.setValueByHash(pDocumentRecord.FormData, tmpDocumentPolyJoinDataAddress, tmpDocumentPolyJoin);
		// The joins carry the instance's GUID, which the processor resolves from the server when the instance is not written here.
		const tmpTestInstanceWrite = this._getRecordWrite('TestInstance', tmpTestInstanceAsRead, tmpTestInstance);
		if (tmpTestInstanceWrite)
		{
			pTestRecordComprehension.TestInstance[tmpTestInstance.GUIDTestInstance] = tmpTestInstanceWrite;
		}
		if (!(tmpTestInstanceSampleJoin.IDTestInstanceSampleJoin > 0))
		{
			pTestRecordComprehension.TestInstanceSampleJoin[tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin] = tmpTestInstanceSampleJoin;
		}
		pTestRecordComprehension.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
	}

	/**
	 * @typedef {Object} TestSectionDescriptor
	 * @property {string} Hash - the section's registration hash
	 * @property {string} [SaltHash]
	 * @property {string} [TestHash]
	 * @property {number} [IDManifestSchema]
	 * @property {number} IDTest - from the section's __Record_Instance bookkeeping (0 when unknown)
	 * @property {string} [GUIDTestInstance]
	 * @property {string} [GUIDDocumentPolyJoin]
	 * @property {boolean} ConciergeManaged - true when the section carries __Record_Instance bookkeeping
	 * @property {string} [RemovedTime] - present on tombstoned sections
	 */

	/**
	 * Describe one dynamic test section of a document record.
	 *
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {string} pSectionHash
	 * @param {Record<string, any>} pSectionEntry - the section's registration entry (from __DynamicSectionData or __RemovedSectionData)
	 *
	 * @return {TestSectionDescriptor}
	 */
	_describeTestSection(pDocumentRecord, pSectionHash, pSectionEntry)
	{
		const tmpDescriptor =
		{
			Hash: pSectionHash,
			SaltHash: pSectionEntry?.SaltHash,
			TestHash: pSectionEntry?.TestHash,
			IDManifestSchema: pSectionEntry?.IDManifestSchema,
			IDTest: 0,
			GUIDTestInstance: undefined,
			GUIDDocumentPolyJoin: undefined,
			ConciergeManaged: false,
			RemovedTime: pSectionEntry?.RemovedTime,
		};
		const tmpSectionData = pDocumentRecord?.FormData?.[pSectionHash];
		if (tmpSectionData && typeof tmpSectionData === 'object')
		{
			for (const tmpKey of Object.keys(tmpSectionData))
			{
				if (tmpKey.endsWith('__Record_Instance'))
				{
					tmpDescriptor.ConciergeManaged = true;
					tmpDescriptor.IDTest = Number(tmpSectionData[tmpKey]?.IDTest) || 0;
					tmpDescriptor.GUIDTestInstance = tmpSectionData[tmpKey]?.GUIDTestInstance;
				}
				else if (tmpKey.endsWith('__Record_DocumentPolyJoin'))
				{
					tmpDescriptor.GUIDDocumentPolyJoin = tmpSectionData[tmpKey]?.GUIDDocumentPolyJoin;
				}
			}
		}
		return tmpDescriptor;
	}

	/**
	 * Enumerate a document record's dynamic test sections: Active (registered in
	 * __DynamicSectionData.Sequence) and Removed (tombstoned in __RemovedSectionData).
	 *
	 * @param {Record<string, any>} pDocumentRecord
	 *
	 * @return {{ Active: Array<TestSectionDescriptor>, Removed: Array<TestSectionDescriptor> }}
	 */
	listTestSectionsInDocumentRecord(pDocumentRecord)
	{
		const tmpResult = { Active: [], Removed: [] };
		const tmpFormData = pDocumentRecord?.FormData;
		if (!tmpFormData)
		{
			return tmpResult;
		}
		const tmpDynamicSectionData = tmpFormData.__DynamicSectionData || { };
		const tmpSequence = Array.isArray(tmpDynamicSectionData.Sequence) ? tmpDynamicSectionData.Sequence : [];
		for (const tmpSectionHash of tmpSequence)
		{
			if (tmpDynamicSectionData[tmpSectionHash])
			{
				tmpResult.Active.push(this._describeTestSection(pDocumentRecord, tmpSectionHash, tmpDynamicSectionData[tmpSectionHash]));
			}
		}
		const tmpRemovedSectionData = tmpFormData.__RemovedSectionData || { };
		for (const tmpSectionHash of Object.keys(tmpRemovedSectionData))
		{
			tmpResult.Removed.push(this._describeTestSection(pDocumentRecord, tmpSectionHash, tmpRemovedSectionData[tmpSectionHash]));
		}
		return tmpResult;
	}

	/**
	 * NOTE: does not save document
	 *
	 * Remove dynamic test sections from a document record by section hash. Each
	 * section's registration moves from __DynamicSectionData (and its Sequence)
	 * to a __RemovedSectionData tombstone; the section's entered data under
	 * FormData[<hash>] is left in place so removal is recoverable.
	 *
	 * @param {Record<string, any>} pDocumentRecord
	 * @param {Array<string>} pSectionHashes
	 *
	 * @return {Array<TestSectionDescriptor>} descriptors of the sections actually removed
	 */
	removeTestSectionsFromDocumentRecord(pDocumentRecord, pSectionHashes)
	{
		if (!pDocumentRecord)
		{
			throw new Error(`No document record provided to removeTestSectionsFromDocumentRecord`);
		}
		const tmpRemoved = [];
		const tmpDynamicSectionData = pDocumentRecord.FormData?.__DynamicSectionData;
		if (!tmpDynamicSectionData || !Array.isArray(pSectionHashes) || pSectionHashes.length === 0)
		{
			return tmpRemoved;
		}
		const tmpRemovedTime = new Date().toISOString();
		for (const tmpSectionHash of pSectionHashes)
		{
			const tmpSectionEntry = tmpDynamicSectionData[tmpSectionHash];
			if (!tmpSectionEntry)
			{
				continue;
			}
			if (!pDocumentRecord.FormData.__RemovedSectionData)
			{
				pDocumentRecord.FormData.__RemovedSectionData = { };
			}
			pDocumentRecord.FormData.__RemovedSectionData[tmpSectionHash] = Object.assign({ }, tmpSectionEntry, { RemovedTime: tmpRemovedTime });
			delete tmpDynamicSectionData[tmpSectionHash];
			tmpRemoved.push(this._describeTestSection(pDocumentRecord, tmpSectionHash, pDocumentRecord.FormData.__RemovedSectionData[tmpSectionHash]));
		}
		if (tmpRemoved.length > 0 && Array.isArray(tmpDynamicSectionData.Sequence))
		{
			const tmpRemovedHashes = new Set(tmpRemoved.map((pSection) => pSection.Hash));
			tmpDynamicSectionData.Sequence = tmpDynamicSectionData.Sequence.filter((pSequenceHash) => !tmpRemovedHashes.has(pSequenceHash));
		}
		return tmpRemoved;
	}

	/**
	 * @param {string} pTestHash - The test hash to get.
	 *
	 * @return {Promise<Record<string, any>>} - The test record for the test hash.
	 */
	async _getTestByHash(pTestHash)
	{
		return await this._get(`Tests/FilteredTo/FBV~Hash~EQ~${pTestHash}`).then((res) => res[0]);
	}

	/**
	 * @param {number} pIDTest - The test ID to get the manifest or RNI for.
	 * @param {number} pIDProject - The Sample's project.
	 * @param {number} pIDMaterial - The Sample's material.
	 *
	 * @return {Promise<Record<string, any>>} - The manifest or RNI for the test ID.
	 */
	async getTestDefinitionForTestID(pIDTest, pIDProject, pIDMaterial)
	{
		// get RNIs
		const tmpRNIs = await this._getAvailableRNIs(pIDProject);
		/**
		 * @type {object|null}
		 */
		let tmpManifest = null;
		const test = await new Promise((resolve, reject) =>
		{
			this.client.getJSON(`Test/${ pIDTest }`, async (pError, pBody) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error getting Test: ${ pError }`, pError);
					return reject(pError);
				}
				if (!pBody)
				{
					this.fable.log.error(`No Test found for ID: ${ pIDTest }`);
					return reject(new Error(`No Test found for ID: ${ pIDTest }`));
				}
				// Use the join-aware resolver so the join table is consulted on this hot path.
				tmpManifest = await this._resolveManifestForTestRecord(pBody);
				resolve(pBody);
			});
		});

		let tmpFallbackRNI;
		for (const tmpRNI of tmpRNIs)
		{
			if (tmpRNI.Options.TestHash == test.Hash)
			{
				tmpRNI.Enabled = true;
				tmpRNI.DisplayName = `Default Test RNI: ${tmpRNI.Name}`;
				tmpRNI.TestHash = tmpRNI.Options.TestHash;
				tmpRNI.TestHashes = tmpRNI.Options.TestHashes;
				tmpRNI.IDTest = pIDTest;
				tmpRNI.SatisfiedIDTests = [pIDTest];
				tmpRNI.SatisfiedTestHashes = [test.Hash];
				return tmpRNI;
			}
			if (Array.isArray(tmpRNI.Options.TestHashes) && tmpRNI.Options.TestHashes.includes(test.Hash))
			{
				tmpFallbackRNI = tmpRNI;
			}
		}
		if (tmpFallbackRNI)
		{
			tmpFallbackRNI.Enabled = true;
			tmpFallbackRNI.DisplayName = `Default Test RNI: ${tmpFallbackRNI.Name}`;
			tmpFallbackRNI.TestHash = test.Hash;
			tmpFallbackRNI.TestHashes = tmpFallbackRNI.Options.TestHashes;
			tmpFallbackRNI.IDTest = pIDTest;
			tmpFallbackRNI.SatisfiedIDTests = [pIDTest];
			tmpFallbackRNI.SatisfiedTestHashes = [test.Hash];
			return tmpFallbackRNI;
		}

		if (tmpManifest)
		{
			tmpManifest.Enabled = true;
			tmpManifest.DisplayName = `Test Hash: ${test.Hash}`;
			tmpManifest.TestHash = test.Hash;
			tmpManifest.TestHashes = [tmpManifest.TestHash];
			tmpManifest.IDTest = pIDTest;
			tmpManifest.SatisfiedIDTests = [pIDTest];
			tmpManifest.SatisfiedTestHashes = [test.Hash];
			return tmpManifest;
		}

		this.fable.log.warn(`No manifest or RNI found for Test ID: ${ pIDTest }`);
		const tmpMissingTestEntry = Object.assign({}, test,
		{
			Enabled: true,
			IDTest: pIDTest,
			SatisfiedIDTests: [pIDTest],
			SatisfiedTestHashes: [test.Hash],
			//LabID: Number(tmpIDLab),
			//LabName: tmpIDLab == '0' ? '' : (tmpParentLabs.find((lab) => lab.IDLab === Number(tmpIDLab)) || {}).Name || `Lab ${tmpIDLab}`,
			DisplayName: test.Name || test.Hash,
			Missing: true,
			TestHash: test.Hash,
		});

		return tmpMissingTestEntry;
	}

	/**
	 * Resolve the ManifestSchema for a given Test ID.
	 *
	 * Resolution order (added 2026-04 to address ManifestSchema hash collisions caused by the
	 * spec-year-2026 overlay deployment):
	 *
	 *   1. Look up TestManifestSchemaJoin rows for this IDTest (scoped by IDCustomer).
	 *      - Exactly 1 join row → deterministic binding; fetch the MS by ID and return.
	 *      - Multiple rows → data error per design; log.fatal and return the lowest-ID match defensively.
	 *      - Zero rows OR the join lookup hard-errors (e.g. table doesn't exist yet pre-migration) →
	 *        fall through to the legacy Hash-based path below. This is the safe-degrade path that
	 *        protects against deployment-order issues (code shipping ahead of schema migration).
	 *   2. Legacy: Test.Hash → getManifestForTestHash → getManifestByHash. First-match-wins on
	 *      collisions (Materials-Form-Manager.js:1110); this is the silent-ambiguity path that
	 *      the join table is here to eliminate for known-collision cases.
	 *
	 * Background: ~/dev/lims_test_routing_investigation_2026-04-28.md §9.1.
	 *
	 * @param {number} pTestID - The test ID to get the manifest for.
	 *
	 * @return {Promise<Record<string, any>>} - The manifest for the test ID.
	 */
	async getManifestForTestID(pTestID)
	{
		if (!pTestID)
		{
			this.fable.log.error('No Test ID provided.');
			throw new Error('No Test ID provided.');
		}

		// Fetch the Test record (need its IDCustomer for the customer-scoped join lookup,
		// and its Hash for the legacy fallback).
		const tmpTest = await new Promise((resolve, reject) =>
		{
			this.client.getJSON(`Test/${pTestID}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error getting Test: ${pError}`, pError);
					return reject(pError);
				}
				if (!pBody)
				{
					this.fable.log.error(`No Test found for ID: ${pTestID}`);
					return reject(new Error(`No Test found for ID: ${pTestID}`));
				}
				return resolve(pBody);
			});
		});

		return this._resolveManifestForTestRecord(tmpTest);
	}

	/**
	 * Resolve a ManifestSchema given an already-fetched Test record. This is the join-aware
	 * resolution path used by both `getManifestForTestID` (after fetching the Test) and internal
	 * call sites in `getMaterialTestDefaults` etc. that already hold a Test record (avoids a
	 * redundant Test refetch on hot paths).
	 *
	 * Resolution order matches `getManifestForTestID`:
	 *   1. TestManifestSchemaJoin lookup by (IDTest, IDCustomer) — preferred, deterministic.
	 *   2. Hash-based lookup via `getManifestForTestHash(Test.Hash)` — legacy fallback.
	 *
	 * Fail-soft: any error in (1) is logged as warn and falls through to (2). Multiple rows in (1)
	 * is a data error — log.fatal fires and the lowest-ID match is returned defensively.
	 *
	 * @param {Record<string, any>} pTest - the Test record (must have IDTest, IDCustomer, Hash)
	 * @return {Promise<Record<string, any>>} the resolved ManifestSchema
	 */
	async _resolveManifestForTestRecord(pTest)
	{
		if (!pTest || !pTest.IDTest)
		{
			throw new Error('No Test record provided to _resolveManifestForTestRecord.');
		}

		let tmpJoinRows = null;
		try
		{
			tmpJoinRows = await new Promise((resolve, reject) =>
			{
				this.client.getJSON(`TestManifestSchemaJoins/FilteredTo/FBV~IDTest~EQ~${pTest.IDTest}~FBV~IDCustomer~EQ~${pTest.IDCustomer}/0/2`,
					(pError, pBody) =>
					{
						if (pError)
						{
							return reject(pError);
						}
						return resolve(Array.isArray(pBody) ? pBody : []);
					});
			});
		}
		catch (pError)
		{
			// Likely causes: table doesn't exist yet (migration hasn't run), endpoint not wired,
			// transient API failure. Don't fail the form lookup — fall through to legacy Hash-based
			// resolution which preserves pre-2026-04 behavior.
			this.fable.log.warn(`TestManifestSchemaJoin lookup failed for IDTest ${pTest.IDTest}; falling back to legacy Hash-based lookup. Expected if schema migration has not yet applied.`,
				{ IDTest: pTest.IDTest, error: pError && pError.message });
			tmpJoinRows = null;
		}

		if (Array.isArray(tmpJoinRows) && tmpJoinRows.length === 1)
		{
			return this.getManifestByID(tmpJoinRows[0].IDManifestSchema);
		}
		if (Array.isArray(tmpJoinRows) && tmpJoinRows.length > 1)
		{
			// Data error per design — TestManifestSchemaJoin is meant to be 1:1 per (IDTest, IDCustomer).
			// Deterministic recovery: pick the lowest IDManifestSchema. Page oncall via log.fatal.
			this.fable.log.fatal(`Multiple TestManifestSchemaJoin rows for IDTest ${pTest.IDTest} (IDCustomer ${pTest.IDCustomer}); expected at most 1`,
				{ IDTest: pTest.IDTest, IDCustomer: pTest.IDCustomer, JoinRows: tmpJoinRows });
			const tmpFirst = [ ...tmpJoinRows ].sort((a, b) => a.IDManifestSchema - b.IDManifestSchema)[0];
			return this.getManifestByID(tmpFirst.IDManifestSchema);
		}

		// Legacy fallback: Hash-based MS lookup.
		return this.getManifestForTestHash(pTest.Hash);
	}

	/**
	 * @param {string} pManifestSchemaHash - The manifest schema hash to get.
	 * @param {number} pCustomerID - The customer ID for scoping.
	 *
	 * @return {Promise<Record<string, any>>} - The manifest for the test hash.
	 */
	async getManifestByHash(pManifestSchemaHash, pCustomerID)
	{
		return new Promise((resolve, reject) =>
		{
			this.client.getJSON(`ManifestSchemas/FilteredTo/FBV~Hash~EQ~${pManifestSchemaHash}`, (pError, pManifestSchemas) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error getting Manifest Schema for hash: ${pManifestSchemaHash}`, pError);
					return reject(pError);
				}
				if (!pManifestSchemas || !Array.isArray(pManifestSchemas) || pManifestSchemas.length === 0)
				{
					this.fable.log.error(`No Manifest Schema found for hash: ${pManifestSchemaHash}`);
					return reject(new Error(`No Manifest Schema found for hash: ${pManifestSchemaHash}`));
				}
				const tmpManifestSchema = pManifestSchemas.find((ms) => ms.IDCustomer == pCustomerID) || pManifestSchemas[0];
				//TODO: consolidate this with getManifestByID below
				this.client.getEntitySetWithPages('ManifestSchemaManifestSchemaPartialJoin', `FBV~IDManifestSchema~EQ~${tmpManifestSchema.IDManifestSchema}`,
					() => { }, () => { }, (pError, pManifestSchemaManifestSchemaPartialJoins) =>
				{
					if (pError)
					{
						this.fable.log.error(`Error getting Manifest Schema Partial Joins for Manifest Schema ID: ${tmpManifestSchema.IDManifestSchema}`, pError);
						return reject(pError);
					}
					if (!pManifestSchemaManifestSchemaPartialJoins || !Array.isArray(pManifestSchemaManifestSchemaPartialJoins))
					{
						this.fable.log.error(`Invalid response for Manifest Schema Partial Joins.`);
						return reject(new Error(`Invalid response for Manifest Schema Partial Joins.`));
					}
					const tmpAnticipate = this.fable.newAnticipate();

					const tmpManifestSchemaPartialsByIDManifestSchema = {};
					const chunkSize = 100;
					for (let i = 0; i < pManifestSchemaManifestSchemaPartialJoins.length; i += chunkSize)
					{
						const chunk = pManifestSchemaManifestSchemaPartialJoins.slice(i, i + chunkSize);
						tmpAnticipate.anticipate(
							(fRequestCallback) =>
							{
								this.client.getEntitySetWithPages('ManifestSchemaPartial', `FBL~IDManifestSchemaPartial~INN~${chunk.map((ms) => ms.IDManifestSchemaPartial).join(',')}`,
									() => { }, () => { }, (pError, pManifestSchemaPartials) =>
								{
									if (pError)
									{
										this.fable.log.error(`Error getting Manifest Schema Partials for Manifest Schema ID: ${tmpManifestSchema.IDManifestSchema}`, pError);
										return fRequestCallback(pError);
									}
									if (!pManifestSchemaPartials || !Array.isArray(pManifestSchemaPartials))
									{
										this.fable.log.error(`Invalid response for Manifest Schema Partials.`);
										return fRequestCallback(pError);
									}
									if (pManifestSchemaPartials.length !== chunk.length)
									{
										this.fable.log.warn(`Mismatch in expected Manifest Schema Partials returned. Expected: ${chunk.length}, Received: ${pManifestSchemaPartials.length}`);
									}
									for (const tmpJoin of chunk)
									{
										const tmpManifestSchemaPartial = pManifestSchemaPartials.find((msp) => msp.IDManifestSchemaPartial === tmpJoin.IDManifestSchemaPartial);
										if (!tmpManifestSchemaPartial)
										{
											this.fable.log.warn(`Manifest Schema Partial not found for ID: ${tmpJoin.IDManifestSchemaPartial}`);
											continue;
										}
										if (!tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema])
										{
											tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema] = [];
										}
										tmpManifestSchemaPartial.Ordinal = tmpJoin.Ordinal;
										tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema].push(tmpManifestSchemaPartial);
									}
									fRequestCallback();
								});
							});
					}
					tmpAnticipate.wait(
						(pError) =>
						{
							if (pError)
							{
								this.fable.log.error(`Error caching Test manifest for Manifest Schema ID: ${tmpManifestSchema.IDManifestSchema}`, pError);
								return reject(pError);
							}
							/** @type {Record<string, any>} */
							const tmpPartials = tmpManifestSchemaPartialsByIDManifestSchema[tmpManifestSchema.IDManifestSchema] || [];
							tmpPartials.sort((a, b) => a.Ordinal - b.Ordinal);
							let tmpMergedConfig = this.fable.services.ManifestPackagingService._mergeManifests(tmpManifestSchema, tmpPartials)?.DefaultFormManifest;
							if (!tmpMergedConfig)
							{
								this.fable.log.error(`Manifest merge failed for Manifest Schema ID: ${tmpManifestSchema.IDManifestSchema}`);
							}
							this._decorateManifestSchema(tmpManifestSchema);
							tmpManifestSchema.Manifest = tmpMergedConfig;
							resolve(tmpManifestSchema);
						},
					);
				});
			});
		});
	}

	/**
	 * @param {number} pManifestSchemaID
	 *
	 * @return {Promise<Record<string, any>>} - The manifest for the test hash.
	 */
	async getManifestByID(pManifestSchemaID)
	{
		return new Promise((resolve, reject) =>
		{
			this.client.getJSON(`ManifestSchema/${pManifestSchemaID}`, (pError, pManifestSchema) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error getting Manifest Schema ID: ${pManifestSchemaID}`, pError);
					return reject(pError);
				}
				if (!pManifestSchema)
				{
					this.fable.log.error(`Invalid response for Manifest Schema.`);
					return reject(new Error(`Invalid response for Manifest Schema.`));
				}
				this.client.getEntitySetWithPages('ManifestSchemaManifestSchemaPartialJoin', `FBV~IDManifestSchema~EQ~${pManifestSchemaID}`,
					() => { }, () => { }, (pError, pManifestSchemaManifestSchemaPartialJoins) =>
				{
					if (pError)
					{
						this.fable.log.error(`Error getting Manifest Schema Partial Joins for Manifest Schema ID: ${pManifestSchemaID}`, pError);
						return reject(pError);
					}
					if (!pManifestSchemaManifestSchemaPartialJoins || !Array.isArray(pManifestSchemaManifestSchemaPartialJoins))
					{
						this.fable.log.error(`Invalid response for Manifest Schema Partial Joins.`);
						return reject(new Error(`Invalid response for Manifest Schema Partial Joins.`));
					}
					const tmpAnticipate = this.fable.newAnticipate();

					const tmpManifestSchemaPartialsByIDManifestSchema = {};
					const chunkSize = 100;
					for (let i = 0; i < pManifestSchemaManifestSchemaPartialJoins.length; i += chunkSize)
					{
						const chunk = pManifestSchemaManifestSchemaPartialJoins.slice(i, i + chunkSize);
						tmpAnticipate.anticipate(
							(fRequestCallback) =>
							{
								this.client.getEntitySetWithPages('ManifestSchemaPartial', `FBL~IDManifestSchemaPartial~INN~${chunk.map((ms) => ms.IDManifestSchemaPartial).join(',')}`,
									() => { }, () => { }, (pError, pManifestSchemaPartials) =>
								{
									if (pError)
									{
										this.fable.log.error(`Error getting Manifest Schema Partials for Manifest Schema ID: ${pManifestSchemaID}`, pError);
										return fRequestCallback(pError);
									}
									if (!pManifestSchemaPartials || !Array.isArray(pManifestSchemaPartials))
									{
										this.fable.log.error(`Invalid response for Manifest Schema Partials.`);
										return fRequestCallback(pError);
									}
									if (pManifestSchemaPartials.length !== chunk.length)
									{
										this.fable.log.warn(`Mismatch in expected Manifest Schema Partials returned. Expected: ${chunk.length}, Received: ${pManifestSchemaPartials.length}`);
									}
									for (const tmpJoin of chunk)
									{
										const tmpManifestSchemaPartial = pManifestSchemaPartials.find((msp) => msp.IDManifestSchemaPartial === tmpJoin.IDManifestSchemaPartial);
										if (!tmpManifestSchemaPartial)
										{
											this.fable.log.warn(`Manifest Schema Partial not found for ID: ${tmpJoin.IDManifestSchemaPartial}`);
											continue;
										}
										if (!tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema])
										{
											tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema] = [];
										}
										tmpManifestSchemaPartial.Ordinal = tmpJoin.Ordinal;
										tmpManifestSchemaPartialsByIDManifestSchema[tmpJoin.IDManifestSchema].push(tmpManifestSchemaPartial);
									}
									fRequestCallback();
								});
							});
					}
					tmpAnticipate.wait(
						(pError) =>
						{
							if (pError)
							{
								this.fable.log.error(`Error caching Test manifest for Manifest Schema ID: ${pManifestSchemaID}`, pError);
								return reject(pError);
							}
							/** @type {Record<string, any>} */
							const tmpPartials = tmpManifestSchemaPartialsByIDManifestSchema[pManifestSchema.IDManifestSchema] || [];
							tmpPartials.sort((a, b) => a.Ordinal - b.Ordinal);
							let tmpMergedConfig = this.fable.services.ManifestPackagingService._mergeManifests(pManifestSchema, tmpPartials)?.DefaultFormManifest;
							if (!tmpMergedConfig)
							{
								this.fable.log.error(`Manifest merge failed for Manifest Schema ID: ${pManifestSchema.IDManifestSchema}`);
							}
							this._decorateManifestSchema(pManifestSchema);
							pManifestSchema.Manifest = tmpMergedConfig;
							resolve(pManifestSchema);
						},
					);
				});
			});
		});
	}

	/**
	 * @param {string} pTestHash - The test hash to get the manifest for.
	 * @param {Record<string, Record<string, any>>} [pOverrideManifestMap] - An optional manifest map to override the default one
	 *
	 * @return {Promise<Record<string, any>>} - The manifest for the test hash.
	 */
	async getManifestForTestHash(pTestHash, pOverrideManifestMap)
	{
		if (pOverrideManifestMap && pOverrideManifestMap[pTestHash])
		{
			return JSON.parse(JSON.stringify({ Manifest: pOverrideManifestMap[pTestHash]}));
		}
		let tmpTestManifestCache = this.fable.AppData._TestManifestCache;
		if (tmpTestManifestCache)
		{
			return tmpTestManifestCache[pTestHash] ? JSON.parse(JSON.stringify(tmpTestManifestCache[pTestHash])) : null;
		}
		return new Promise((resolve, reject) =>
		{
			this.client.getEntitySetWithPages('ManifestSchema', 'FBV~Type~EQ~Test', () => { }, () => { }, (pError, pManifestSchemas) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error caching Test manifest schemas for Test Hash: ${pTestHash}`, pError);
					return reject(pError);
				}
				if (!pManifestSchemas || !Array.isArray(pManifestSchemas))
				{
					this.fable.log.error(`No Manifest Schemas found for Tests.`);
					return reject(new Error(`No Manifest Schemas found for Tests.`));
				}

				this.client.getEntitySetWithPages('ManifestSchemaManifestSchemaPartialJoin', `FBL~IDManifestSchema~INN~${pManifestSchemas.map((ms) => ms.IDManifestSchema).join(',')}`,
					() => { }, () => { }, (pError, pManifestSchemaManifestSchemaPartialJoins) =>
				{
					if (pError)
					{
						this.fable.log.error(`Error caching Test manifest schema partials for Test Hash: ${pTestHash}`, pError);
						return reject(pError);
					}
					if (!pManifestSchemaManifestSchemaPartialJoins || !Array.isArray(pManifestSchemaManifestSchemaPartialJoins))
					{
						this.fable.log.error(`Invalid response for Manifest Schema Partial Joins.`);
						return reject(new Error(`Invalid response for Manifest Schema Partial Joins.`));
					}
					const tmpAnticipate = this.fable.newAnticipate();

					const tmpManifestSchemaPartialsByIDManifestSchema = {};
					const chunkSize = 100;
					for (let i = 0; i < pManifestSchemaManifestSchemaPartialJoins.length; i += chunkSize)
					{
						const chunk = pManifestSchemaManifestSchemaPartialJoins.slice(i, i + chunkSize);
						tmpAnticipate.anticipate(
							(fRequestCallback) =>
							{
								this.client.getEntitySetWithPages('ManifestSchemaPartial', `FBL~IDManifestSchemaPartial~INN~${chunk.map((ms) => ms.IDManifestSchemaPartial).join(',')}`,
									() => { }, () => { }, (pError, pManifestSchemaPartials) =>
								{
									if (pError)
									{
										this.fable.log.error(`Error caching Test manifest schema partials for Test Hash: ${pTestHash}`, pError);
										return fRequestCallback(pError);
									}
									if (!pManifestSchemaPartials || !Array.isArray(pManifestSchemaPartials))
									{
										this.fable.log.error(`Invalid response for Manifest Schema Partials.`);
										return fRequestCallback(pError);
									}
									if (pManifestSchemaPartials.length !== chunk.length)
									{
										this.fable.log.warn(`Mismatch in expected Manifest Schema Partials returned. Expected: ${chunk.length}, Received: ${pManifestSchemaPartials.length}`);
									}
									for (const tmpManifestSchemaPartial of pManifestSchemaPartials)
									{
										if (!tmpManifestSchemaPartialsByIDManifestSchema[tmpManifestSchemaPartial.IDManifestSchema])
										{
											tmpManifestSchemaPartialsByIDManifestSchema[tmpManifestSchemaPartial.IDManifestSchema] = [];
										}
										tmpManifestSchemaPartialsByIDManifestSchema[tmpManifestSchemaPartial.IDManifestSchema].push(tmpManifestSchemaPartial);
									}
									fRequestCallback();
								});
							});
					}
					tmpAnticipate.wait(
						(pError) =>
						{
							if (pError)
							{
								this.fable.log.error(`Error caching Test manifest for Test Hash: ${pTestHash}`, pError);
								return reject(pError);
							}
							tmpTestManifestCache = {};
							for (const tmpManifestSchema of pManifestSchemas)
							{
								let tmpMergedConfig = this.fable.services.ManifestPackagingService._mergeManifests(tmpManifestSchema, tmpManifestSchemaPartialsByIDManifestSchema[tmpManifestSchema.IDManifestSchema] || [])?.DefaultFormManifest;
								if (tmpTestManifestCache[tmpManifestSchema.SubType])
								{
									// this causes us to merge manifest whose SubType collides on the same hash
									tmpMergedConfig = this.fable.services.ManifestPackagingService._simpleManifestMerge(
										JSON.parse(JSON.stringify(tmpTestManifestCache[tmpManifestSchema.SubType])),
										tmpMergedConfig);
								}
								if (!tmpMergedConfig)
								{
									this.fable.log.error(`Manifest merge failed for Manifest Schema ID: ${tmpManifestSchema.IDManifestSchema}`);
									continue;
								}
								this._decorateManifestSchema(tmpManifestSchema);
								tmpManifestSchema.Manifest = tmpMergedConfig;
								tmpTestManifestCache[tmpManifestSchema.SubType] = tmpManifestSchema;
							}
							resolve(tmpTestManifestCache[pTestHash] ? JSON.parse(JSON.stringify(tmpTestManifestCache[pTestHash])) : null);
						},
					);
				});
			});
		});
	}

	/**
	 * @param {number} [pLength=8]
	 * @return {string} a random salt to identify this input instance
	 */
	_generateSaltStr(pLength = 8)
	{
		let result = '';
		for (let i = 0; i < pLength; i++)
		{
			result += SALT_CHARS.charAt(Math.floor(Math.random() * SALT_CHARS.length));
		}
		return result;
	}
}

module.exports = MaterialsFormManager;
/** @type {Record<string, any>} */
module.exports.default_configuration = { };
module.exports.LIMS_GUID_PATTERNS = LIMS_GUID_PATTERNS;
module.exports.isLIMSForm = isLIMSForm;
module.exports.getLIMSFormCandidates = getLIMSFormCandidates;
module.exports.FORM_GROUPS = FORM_GROUPS;
